import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { localised, useI18n, type CopyKey } from './i18n';
import Curriculum from './Curriculum';
import Disclosure from './Disclosure';
import Research from './Research';
import ResearchPulse from './ResearchPulse';
import Sources from './Sources';
import ObjectiveTabs from './ObjectiveTabs';
/*
 * The jumps between panels, in a module of their own since F-129 — `Sources` owning the uploader
 * is the reason they are scrolls rather than a second file control, and since F-141 the research
 * section is reached the same way, because it is a panel on this screen and no longer a route.
 */
import { goToResearch, goToUpload, RESEARCH_PANEL_ID } from './panels';
import { ROUTES } from './routes';
import {
  closeObjective,
  createObjective,
  currentObjective,
  activateObjective,
  getCoverage,
  getCurriculum,
  getResearchProgress,
  startItemGeneration,
  isClosed,
  isError,
  listObjectives,
  reactivateObjective,
  startReresearch,
  startResearch,
  startSynthesis,
  updatePlanSource,
  updateTarget,
  type CoverageView,
  type Objective,
  type PlanNode,
  type ResearchProgress,
} from './api';
import type { Locale, ObjectiveStatus, PlanSource } from '@nabu/shared';
/*
 * The plan screen's own decision, in a module with no DOM in it.
 *
 * Imported rather than written inline for the reason `chat/keyboard.ts` gives: which panel this
 * screen offers is the judgement F-118 can most easily get wrong — an objective that opted out of
 * research being shown the research button, or one with nothing uploaded yet being shown a
 * spinner — and a decision buried in JSX cannot be tested from plain Node.
 */
import { isUploadsOnly, offersResearch, planStep } from './plan/step';
/* How far through their first run this learner is, read from the one place that asks. F-141. */
import { useFirstRun } from './onboarding/FirstRun';
/*
 * Which panel owns the live research panel, now that both of them are on one screen (F-141).
 *
 * In a DOM-free module for the same reason `planStep` is, and with one of its own: two
 * `ResearchPulse`s rendered at once is the two-lists-that-disagree failure F-129 was written to
 * end, and `npm test` cannot see a `.tsx` file to catch it.
 */
import { ownsPulse } from './plan/view';
/*
 * What the run itself is doing, which the objective's status does not say.
 *
 * Two of this screen's panels need it and neither could ask before: the one that must not offer
 * to build a plan out of research that is still arriving (F-146), and the one that has to say
 * what the findings *are* rather than that they are in (F-145).
 *
 * `researchArriving` rather than `stillComing`: this screen keeps the last progress reading it
 * could get, so a run that stopped without saying so would otherwise withhold the build button
 * for the rest of the session. See the staleness window where it is declared.
 */
import { pulseOf, researchArriving, stillComing } from './plan/pulse';

/**
 * One objective at a time.
 *
 * Which is why this is not a form plus a list. Before there is an objective, the
 * screen asks for one; after, the screen *is* the objective — its generated name
 * as the heading, what the learner asked for underneath, and the target as the
 * one thing still adjustable. Closing it is what opens the next.
 *
 * The closed ones underneath are not an exception to that. They are the record of
 * what has been worked on, and the only way back into one — a learner who rotates
 * between two subjects should not have to retype an objective to return to it, and
 * without somewhere to see them, closing anything feels like deleting it.
 *
 * The target is saved on its own. Bundling it with the description meant moving
 * the slider resubmitted the description — and since the form cleared on save,
 * that failed with "description required", which is both wrong and baffling.
 *
 * ---
 *
 * **One component, three destinations.** This file used to be the whole signed-in app:
 * the objective, its plan, its questions, the research behind it, the uploader, the
 * schedule, the progress panel, email, mail preferences and the delete button, in one
 * scroll. The menu cuts that into sections, and `section` selects which panels of it a
 * route renders — plan, research and data, or the target under configuration.
 *
 * **It is not four files, and that is the decision worth defending.** Almost everything
 * above this line is one object's lifecycle: flags that must outlive their own request
 * but not the status change that ends them, an elapsed check that exists because a pass
 * can fail between two polls, a ref that watches a lock disappear, and a poll whose only
 * stop condition is that its own dependency went away. Split across files, each piece
 * would need either its own copy of that machine or a context holding it — and the
 * invariants that make it correct are exactly the kind that rot when they are three
 * imports away from the panel they protect. Splitting the *rendering* by section costs
 * one prop; splitting the *state* would cost the reasoning. What each route pays for
 * that choice is a fetch of the objective on arrival, which it would have paid anyway.
 */

/**
 * Errors that mean the list on screen no longer describes what is stored.
 *
 * All three are answered by reloading and saying so, rather than by retrying:
 * whichever objective he tapped, the decision was made against a list that has
 * since moved, and a silent retry would act on the same stale reading.
 */
const STALE_ERRORS = ['conflict', 'not_found', 'not_closed'];

/** The statuses in which a curriculum can exist, and so the ones worth asking for. */
const CURRICULUM_STATUSES: readonly ObjectiveStatus[] = ['review', 'active'];

/**
 * How long to leave between polls while the outline is being written.
 *
 * **Five seconds, and it is the one cadence this screen used not to have.** The outline call takes
 * about forty seconds and writes every subject at once, so that minute is the only part of a
 * sixteen-minute wait where something genuinely appears — and it is the minute in which somebody
 * decides whether this product is worth waiting for. `Research.tsx` polled it at five seconds
 * while it was a screen of its own and `Landing` sent every waiting learner there; merging the two
 * without this would have made the first minute of the product strictly slower than it was.
 *
 * The waves that follow are Message Batches measured at six to 109 minutes on this workspace, so
 * they keep the fifteen-second cadence: a page asking every five seconds through ninety minutes of
 * that is thousands of requests to watch one number not change.
 *
 * **It paces `load`, which is more than the progress query** — the objective, the run, the closed
 * list, and the plan and its coverage where those apply. `Research.tsx` spent five seconds on one
 * query; this spends it on three or four. Over a forty-second outline that is tens of requests,
 * once per objective, and the alternative is a second timer for one panel, which is the thing this
 * merge exists to stop.
 */
const POLL_OUTLINE_MS = 5_000;

/**
 * The statuses in which a gap list describes the plan that is actually on screen.
 *
 * Gaps are written by synthesis, so they only exist from `review` onwards. The
 * restriction matters most in the other direction: while a rebuild is running the
 * stored list is the *previous* pass's, and showing it beside "building the
 * curriculum" would ask somebody to go and photograph pages for gaps that may be
 * about to close on their own.
 */
const GAP_STATUSES: readonly ObjectiveStatus[] = ['review', 'active'];

/**
 * How many gaps are shown before the rest fold away.
 *
 * A gap is a short phrase, so six of them is a few lines and reads at a glance.
 * Nothing caps how many synthesis can emit, though, and twenty rendered flat would
 * push the uploader that closes them off the bottom of a phone — which is the one
 * thing this notice must not do, since the upload is the only reason it is worth
 * showing at all. Folding is how it stays short without anything being hidden from
 * somebody who wants the whole list.
 */
const GAPS_SHOWN = 6;

/*
 * The price that used to be on the "look for what is missing" button, and why there is none.
 *
 * It named $2.38 and then $0.80, because "look again" used to be a whole research pass and is
 * now the plan-level half of a pair: an outline told what the plan already covers, asked only
 * for what is not there, and a wave of deepening over whatever that turns up.
 *
 * **The figure itself is gone from the interface** (F-110). Leo, 2026-09-15: *"the user should
 * not know about budget, we will hide that in our business model… they will pay a monthly flat
 * fee so budget is our concern."* What a pass is allowed to spend is still enforced, in
 * `allowanceFor` in `packages/ingest/src/budget.ts`, where a tier will one day set it per
 * account — it is simply not a thing a learner is asked to reason about before pressing a
 * button. The honesty this loses is the honesty about our costs; the honesty that matters to
 * them — that it may find nothing, and that it changes nothing until they say so — is still on
 * the panel.
 */

/**
 * The panel that owns writing the questions, and no longer the end of a journey.
 *
 * **Writing the questions is not an optional tidy-up after a fold; it is the step
 * that makes the new material reachable at all.** A node with no items is never
 * asked — the sweep works off review states and those are written per item — so a
 * plan that visibly gained a topic while the phone gained nothing is exactly the
 * "everything green, nothing arrives" failure this project keeps producing.
 *
 * Which is why a fold no longer *points* here. It used to end with a button that
 * scrolled to this panel so the learner could press the panel's own button — a
 * control whose entire effect was to move the viewport towards the control that did
 * the work, and the fourth press in a sequence of four. The fold starts generation
 * itself now (F-92); this id stays because the panel is still where somebody asks for
 * the questions to be written *again*, which is a real thing to want and not part of
 * the automatic path.
 */
const QUESTIONS_PANEL_ID = 'your-questions';

/**
 * What the objective asked for and the sources did not carry.
 *
 * **A gap is not an omission and it is not a question. It is a request for
 * material** — see D-30 and D-31. These used to reach a learner as curriculum: he
 * was asked, in earnest, whether the researcher had managed to open a WHO
 * document. The gap is the one piece of a research document's provenance he can
 * actually act on, because the point locations missing from the web are on every
 * page of the book on his desk.
 *
 * So this reads as an invitation and never as an apology. The plan is not broken;
 * it is incomplete in a way this learner is uniquely able to close, and the only
 * action offered is the upload that closes it — the notice is worth showing at all
 * only because there is something to do about it.
 *
 * Nothing at all when there are none, and no reassuring empty box either. An
 * objective whose sources covered everything should look like nothing happened.
 */
function Gaps({
  gaps,
  status,
}: {
  gaps: string[] | undefined;
  status: ObjectiveStatus;
}) {
  const { t } = useI18n();
  const [all, setAll] = useState(false);

  if (!gaps || gaps.length === 0) return null;
  if (!GAP_STATUSES.includes(status)) return null;

  const shown = all ? gaps : gaps.slice(0, GAPS_SHOWN);
  const rest = gaps.length - shown.length;

  return (
    <section className="panel">
      <h2>{t('gap.title')}</h2>
      <p className="muted">{t('gap.body')}</p>

      {/* The phrases as synthesis wrote them, in the learner's own language and
          in his own terms — "the body location of each point", not a node id and
          not a sentence about a source. Rewriting them here would put this screen
          in the business of paraphrasing the thing it is asking him to recognise. */}
      <ul className="gaps">
        {shown.map((gap, index) => (
          <li key={`${index}:${gap}`}>{gap}</li>
        ))}
      </ul>

      {(rest > 0 || all) && (
        <button type="button" className="linky" onClick={() => setAll(!all)}>
          {all ? t('gap.less') : `${t('gap.more')} (${rest})`}
        </button>
      )}

      {/*
       * What happens after the upload, and it differs by status because what he
       * can do differs by status. Gaps are recomputed by synthesis, which from
       * `review` replaces the plan and from `active` extends it — two different
       * promises, and the running one is the one that used to be a lie: this line
       * told him the plan would stay as it was until some later build, above a
       * button asking for a photograph. F-87 put the button that folds it in
       * directly below this panel, and this line now points at it.
       */}
      <p className="muted small">
        {status === 'active' ? t('gap.after.active') : t('gap.after.review')}
      </p>

      <div className="actions">
        <button type="button" className="button primary" onClick={goToUpload}>
          {t('gap.action')}
        </button>
      </div>
    </section>
  );
}

/**
 * How often the screen asks, once the waiting is a wave rather than an outline.
 *
 * Fifteen seconds, which is nothing next to a wait measured in minutes and cheap enough to leave
 * running on a phone. The faster cadence for the one minute where something actually appears is
 * `POLL_OUTLINE_MS` above.
 *
 * It is also the window the two in-flight flags hand over to storage in: a poll interval after
 * the request, an unlocked row means the pass is over or never started.
 */
const POLL_INTERVAL_MS = 15_000;

/**
 * How long the screen is prepared to sit and wait before it says so.
 *
 * Sized for research, the longer of the two waits: five to fifteen minutes is what a pass takes,
 * so the ceiling has to sit past the top of that range rather than in the middle of it, where it
 * would give up on passes that were about to land. It is bounded at all because the alternative
 * is a tab left open overnight reloading a finished objective for ever, and because a pass that
 * is twenty minutes late is a real failure worth saying out loud rather than hiding behind a
 * spinner that never resolves.
 *
 * **Twenty minutes on the clock, and it used to be eighty polls** — the same number while the
 * interval was one number, and wrong at one of two cadences the moment it was not. Eighty polls
 * is twenty minutes of waves and under seven minutes of outline, so an outline that hung would
 * have given up three times sooner than the thing this is supposed to describe. `Research.tsx`
 * had already learned this and said so, and its comment is why it is a duration here.
 */
const POLL_WINDOW_MS = 20 * 60 * 1_000;

/**
 * How long the screen waits when what it is waiting for is a research run.
 *
 * **Two hours, and this is `MAX_WATCH_MS` restored rather than invented.** `Research.tsx` watched
 * a run for two hours because a wave is a Message Batch and batches on this workspace were
 * measured at six to 109 minutes; `Landing` sent every waiting learner to that screen, so two
 * hours is what the wait a learner actually sits through has always been given. Twenty minutes is
 * sized for synthesis and the fold, which take minutes, and applying it to a 109-minute batch
 * would be this merge quietly taking something away — the review's finding, and it was right.
 *
 * It only ever extends the window and never shortens it: a run that finishes cannot retract time
 * the screen has already committed to, or the "this is taking a while" notice would arrive at the
 * exact moment the thing arrived.
 */
const RESEARCH_WINDOW_MS = 2 * 60 * 60 * 1_000;

/**
 * The generated title in the reader's language.
 *
 * The fallback itself lives in i18n.ts, because curriculum nodes need exactly the
 * same one and two of them would eventually disagree — see `localised`.
 */
function titleOf(objective: Objective, locale: Locale, fallback: string): string {
  return localised(objective.title, locale, fallback);
}

/**
 * Most recently closed first, which is not the order the API returns.
 *
 * That list is ordered by when each objective was *created*, which is right for
 * picking out the one just made and wrong here: something long-standing that was
 * shelved a minute ago would sort below newer objectives closed weeks earlier, in
 * a list whose every row is captioned with a closing date. The visible ordering
 * would contradict the visible dates.
 *
 * Rows closed before `closedAt` existed fall back to their creation date. That is
 * not a guess about when they closed — nothing knows that — it just keeps them in
 * a stable position among themselves instead of jumping to the top.
 */
function byMostRecentlyClosed(a: Objective, b: Objective): number {
  return (b.closedAt ?? b.createdAt).localeCompare(a.closedAt ?? a.createdAt);
}

/** The target control, shared by the create form and the live objective. */
/**
 * The three answers to "where does the material come from", as a choice the learner recognises.
 *
 * One component for both moments, exactly as `TargetFields` is: it is asked when the objective is
 * stated and changed under settings, and the two must not be two controls that can disagree about
 * what the options are.
 *
 * **The hints are the feature and are not decoration.** The labels alone — "find it for me",
 * "I already have it", "start from mine and fill in the gaps" — do not say that the second will
 * never look anything up, and the learner choosing it is choosing exactly that. No hint mentions
 * money: uploads-only is the cheapest thing this product does and the interface says nothing
 * about it, which is Leo's standing instruction about budgets and tiers.
 *
 * **The order is the order somebody decides in, and the middle one is F-119's.** It is offered
 * between the two it sits between because that is what it is: the learner has material, and
 * wants more than it. Putting it last would read as an advanced option; putting it first would
 * ask somebody with nothing uploaded to choose it.
 */
function PlanSourceFields({
  planFrom,
  setPlanFrom,
  disabled,
}: {
  planFrom: PlanSource;
  setPlanFrom: (next: PlanSource) => void;
  disabled: boolean;
}) {
  const { t } = useI18n();
  return (
    <fieldset className="field">
      <span>{t('plan.source.label')}</span>
      <div className="choices">
        {(['research', 'scoped', 'uploads'] as const).map((option) => (
          <label className="choice" key={option}>
            <input
              type="radio"
              // Its own group name, distinct from the target's `mode`: two radio sets sharing a
              // name in one form is one set, and choosing a target would clear this.
              name="planFrom"
              checked={planFrom === option}
              onChange={() => setPlanFrom(option)}
              disabled={disabled}
            />
            <span>{t(`plan.source.${option}` as const)}</span>
          </label>
        ))}
      </div>
      {/* One hint, for the option that is chosen. Three hints at once is a page of prose in
          front of a decision that is three words long, and the one that matters is the one
          describing what is about to happen. */}
      <small>{t(`plan.source.${planFrom}.hint` as const)}</small>
    </fieldset>
  );
}

function TargetFields({
  mode,
  setMode,
  score,
  setScore,
  date,
  setDate,
  disabled,
}: {
  mode: 'score' | 'date';
  setMode: (m: 'score' | 'date') => void;
  score: number;
  setScore: (n: number) => void;
  date: string;
  setDate: (s: string) => void;
  disabled: boolean;
}) {
  const { t } = useI18n();
  return (
    <fieldset className="field">
      <span>{t('obj.target.label')}</span>
      <div className="choices">
        {(['score', 'date'] as const).map((m) => (
          <label className="choice" key={m}>
            <input
              type="radio"
              name="mode"
              checked={mode === m}
              onChange={() => setMode(m)}
              disabled={disabled}
            />
            <span>{t(m === 'score' ? 'obj.target.score' : 'obj.target.date')}</span>
          </label>
        ))}
      </div>

      {mode === 'score' ? (
        <>
          <input
            type="range"
            min={70}
            max={97}
            value={score}
            onChange={(e) => setScore(Number(e.target.value))}
            disabled={disabled}
          />
          <small>
            {score}% — {t('obj.target.scoreHint')}
          </small>
        </>
      ) : (
        <>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            // Tomorrow at the earliest: the API refuses a past date, and offering
            // one in the picker only invites the error.
            min={new Date(Date.now() + 86_400_000).toISOString().slice(0, 10)}
            disabled={disabled}
            required
          />
          <small>{t('obj.target.dateHint')}</small>
        </>
      )}
    </fieldset>
  );
}

/**
 * What has been closed, and the way back into it.
 *
 * Rendered on both screens — with something live and without — because "switch to
 * another one" and "start again on an old one" are the same act from the learner's
 * side, and giving them two different places to look would hide whichever one he
 * happens not to be on.
 *
 * A disclosure rather than a list, and nothing at all when there is no history:
 * on a phone this sits under a screen that already scrolls, and a first objective
 * should not open beneath a box whose only content is that there is no history.
 */
function ClosedObjectives({
  items,
  live,
  busy,
  note,
  problem,
  open,
  onOpenChange,
  onReactivate,
}: {
  items: Objective[];
  live: Objective | null;
  busy: boolean;
  note?: string;
  problem?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReactivate: (objective: Objective) => void;
}) {
  const { t, locale } = useI18n();

  if (items.length === 0) return null;

  const closedOn = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return (
    <section className="panel">
      <details className="history" open={open} onToggle={(e) => onOpenChange(e.currentTarget.open)}>
        <summary>
          <h2>{t('obj.history.title')}</h2>
          <span className="muted small">{items.length}</span>
        </summary>

        <p className="muted">{live ? t('obj.history.switchHint') : t('obj.history.hint')}</p>

        {problem && (
          <p className="problem" role="alert">
            {problem}
          </p>
        )}
        {note && (
          <p className="muted" role="status">
            {note}
          </p>
        )}

        <ul className="objectives">
          {items.map((objective) => (
            <li key={objective.objectiveId}>
              <h3>{titleOf(objective, locale, t('obj.history.untitled'))}</h3>
              <p className="meta">
                <span className={`pill pill-${objective.status}`}>
                  {t(`obj.status.${objective.status}` as CopyKey)}
                </span>{' '}
                {/* Dated from `closedAt` alone. Objectives closed before that field
                    existed have none, and dating them from `createdAt` would put a
                    confident wrong date on the one thing this row is here to say. */}
                {objective.closedAt && (
                  <>
                    {t('obj.history.closed')} {closedOn.format(new Date(objective.closedAt))}
                  </>
                )}
              </p>
              <div className="actions">
                <button
                  type="button"
                  className="button"
                  disabled={busy}
                  onClick={() => onReactivate(objective)}
                >
                  {t('obj.history.reactivate')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}

/**
 * What a section that works on an objective shows when there is not one.
 *
 * Not a panel with disabled controls in it, and not an empty screen. An empty screen is
 * the same thing a failed load looks like, and this is the one state where a learner is
 * most likely to conclude the app is broken — they have just installed it and tapped
 * around the menu. It names the state and gives the one tap that leaves it.
 */
function NoObjective() {
  const { t } = useI18n();
  return (
    <section className="panel">
      <h2>{t('plan.none.title')}</h2>
      <p className="muted">{t('plan.none.body')}</p>
      <div className="actions">
        {/* The named route and not the literal, for F-142's reason: a path typed into a
            component is a path a rename cannot find. */}
        <Link className="button primary" to={ROUTES.plan}>
          {t('plan.none.action')}
        </Link>
      </div>
    </section>
  );
}

/**
 * Which of this screen's panels a route is asking for.
 *
 * **Two, where there were three** (F-141). `research` named the half of this component that
 * rendered what had been read and what to do about it, at `/research`; it is not a route any
 * more, and its panels are rendered by `plan` beneath the plan they are about. What is left is
 * the objective's own screen and the two settings that act on it from configuration.
 */
export type ObjectiveSection = 'plan' | 'settings';

export default function Objectives({ section }: { section: ObjectiveSection }) {
  const { t, locale } = useI18n();
  const [search, setSearch] = useSearchParams();
  /*
   * Which plan this screen is about (F-151 step 8), from the same `?o=` a notification carries.
   *
   * Absent on the configuration screen's own two panels as well, and harmlessly: they act on
   * whichever plan `load` settled on, and configuration has no tab bar because messaging
   * settings are global by Leo's decision — *"Messaging settings is global, I define globally"*.
   */
  const objectiveId = search.get('o') ?? undefined;
  /*
   * The first run, for one moment only: stating an objective is one of the two acts that change
   * which stage of it a learner is at, and this screen is where it happens.
   *
   * `note` rather than `reread`, because the create route answers with the status it wrote —
   * asking the objectives index again would be asking an eventually consistent read to confirm
   * something already in hand, which is what bounced `/welcome` in the first version of this.
   */
  const { facts: firstRunFacts, note: noteFirstRun } = useFirstRun();
  const navigate = useNavigate();
  const [objective, setObjective] = useState<Objective | null | undefined>();
  /**
   * Every live plan, for the tabs. `undefined` until the read lands or when it failed.
   *
   * Read out of the call this screen already makes rather than by a second request: `load`
   * asks `GET /api/objectives/current`, which has answered with the whole list since step 6.
   */
  const [objectives, setObjectives] = useState<Objective[] | undefined>();
  /**
   * The plan the newest `load` was started for. See `load` — it is the stale-answer guard.
   *
   * A ref and not state: nothing renders from it, and it has to be readable by a promise that
   * was created before the render that would have updated a state value.
   */
  const loadingFor = useRef<string | undefined>(undefined);
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState<'score' | 'date'>('score');
  /*
   * Where the material comes from, defaulted to research on a blank form.
   *
   * **The default is the answer somebody who does not read the question gets**, and it is
   * research for the reason `DEFAULT_PLAN_SOURCE` gives: the wrong default in the other
   * direction leaves a learner waiting for pages they were never asked for, in silence. The
   * radio is pre-selected rather than left empty so nothing has to be pressed to get today's
   * behaviour.
   */
  const [planFrom, setPlanFrom] = useState<PlanSource>('research');
  /* The settings screen's own two messages, for `saved`'s reason: one control, one answer. */
  const [sourceSaved, setSourceSaved] = useState(false);
  const [score, setScore] = useState(85);
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  const [saved, setSaved] = useState(false);
  const [closed, setClosed] = useState<Objective[]>([]);
  // The history keeps its own two messages. Sharing `problem` with the form above
  // would report a failed reactivation next to the target slider, pinning the
  // complaint to a control that had nothing to do with it.
  const [closedProblem, setClosedProblem] = useState<string | undefined>();
  const [closedNote, setClosedNote] = useState<string | undefined>();
  /*
   * Whether the history is expanded, `undefined` until he says. It is held here
   * rather than inside the section because the two screens are different branches
   * of this render: bringing an objective back moves him from one to the other,
   * which unmounts the section, and state kept in there would take the expansion —
   * and the confirmation message inside it — down with it.
   */
  const [closedOpen, setClosedOpen] = useState<boolean>();
  /*
   * The curriculum, `undefined` until it has been asked for. Distinct from `[]`
   * on purpose: an empty array is "nothing built yet", which is what the offer to
   * build one is written against, and treating "not asked yet" as the same thing
   * would flash that offer under an objective that already has a plan.
   */
  const [nodes, setNodes] = useState<PlanNode[] | undefined>();
  /**
   * What happened to every researched subject, and which of them reached this plan (F-129).
   *
   * Read here rather than inside `Curriculum` for the reason the nodes are: this component
   * already knows whether the objective is in a state that can have a plan at all, and asking
   * from the panel would mean asking before knowing — a 404 for every draft on screen, which is
   * noise that buries the real ones.
   */
  const [coverage, setCoverage] = useState<CoverageView | undefined>();
  /**
   * Where the research run itself has got to (F-144 to F-147).
   *
   * **Read on the plan screen, which had no idea any of this existed.** The screen a learner
   * lands on after pressing the button knew one thing about a sixteen-minute wait — the
   * objective's status — and the rows that say which subjects are open, which have settled and
   * how much each of them found were a route away on a screen nobody thinks to visit. `Research`
   * polls its own copy on that other screen, so nothing here fetches for it: two pollers for one
   * answer on one screen is how two panels come to disagree about it.
   *
   * `undefined` means "not read yet", which is the state F-144's first second is in, and not
   * "there is no run" — the API answers `{ run: undefined }` for that and `getResearchProgress`
   * turns it into this same absence, which is correct for every panel that reads it: both mean
   * there is nothing to show yet.
   */
  const [progress, setProgress] = useState<ResearchProgress | undefined>();
  /**
   * How long to wait before the next poll, held in a ref rather than in state.
   *
   * The loop below reads it each time round rather than taking it from a dependency, because a
   * dependency that changed when the run state changed would tear the loop down and start a new
   * one — which restarts its clock, which means the "this page has stopped checking" escape never
   * arrives. A ref changes nothing about identity, so the cadence moves between two polls without
   * the loop noticing it was ever different.
   */
  const pollEvery = useRef(POLL_INTERVAL_MS);
  /**
   * How long the loop is prepared to keep going, read the same way and for the same reason.
   *
   * A research run in flight buys two hours; everything else gets twenty minutes. Read each time
   * round rather than fixed on entry, because the wait usually *starts* before the run row exists
   * — `starting` is what sets the loop going, and the outline is written a poll or two later.
   */
  const pollWindow = useRef(POLL_WINDOW_MS);
  useEffect(() => {
    const outlining = progress?.run.state === 'outlining';
    pollEvery.current = outlining ? POLL_OUTLINE_MS : POLL_INTERVAL_MS;
    pollWindow.current = stillComing(progress?.run.state) ? RESEARCH_WINDOW_MS : POLL_WINDOW_MS;
  }, [progress?.run.state]);
  /*
   * Generation has its own two messages rather than sharing the step panel's.
   *
   * It is the one action here that does not move the status, so it cannot be
   * reported by the status-driven panel above — and a batch runs for minutes after
   * the request returns, so its "started" note has to survive on its own.
   */
  const [itemsNote, setItemsNote] = useState<string | undefined>();
  const [itemsProblem, setItemsProblem] = useState<string | undefined>();
  const [writingItems, setWritingItems] = useState(false);
  const [starting_, setStarting_] = useState(false);
  /*
   * Starting has its own error, rather than borrowing the questions panel's.
   *
   * `itemsProblem` is rendered in two places — beside the questions and beside the
   * rebuild offer — so a failure from Start appeared twice, in two panels that had
   * nothing to do with it. One piece of state read in more than one place is not a
   * shared message, it is the same complaint pinned to unrelated controls.
   */
  const [startProblem, setStartProblem] = useState<string | undefined>();
  /*
   * Folding new material into a running plan: its own flag, its own message.
   *
   * Not `starting` and not `stepProblem`, and neither is a stylistic choice. Those
   * two are cleared when the *status* changes, and this is the one action on this
   * screen that deliberately never changes it — a running objective that moves off
   * `active`, even for the ninety seconds a pass takes, stops the learner's
   * questions. A flag waiting for a status change that will never come is a button
   * that stays dead for the rest of the session.
   *
   * The message is separate for the reason `startProblem` was separated out: one
   * piece of error state read in two panels is not a shared message, it is the same
   * complaint pinned to unrelated controls, and it reached Leo as "the error came up
   * in multiple places".
   */
  const [folding, setFolding] = useState(false);
  const [foldProblem, setFoldProblem] = useState<string | undefined>();
  /*
   * Asking research to look again: its own flag, its own message, its own clock.
   *
   * Everything the fold's own state says applies here and one thing more. This is
   * the only control on the screen that spends $2.38 when it is pressed, so the
   * flag exists less to animate a button than to make sure a second press cannot
   * happen while the first one is still in the air — the API refuses one, but a
   * button that looks pressable while a pass is starting is an invitation to find
   * that out the expensive way.
   *
   * Not `starting` and not `stepProblem`: those are cleared when the status
   * changes, and a re-research deliberately never changes it. A flag waiting for a
   * status change that will never come is a button dead for the rest of the
   * session.
   */
  const [looking, setLooking] = useState(false);
  const [lookProblem, setLookProblem] = useState<string | undefined>();
  /*
   * What the learner says is missing, in their own words (F-109).
   *
   * The automatic `gaps` synthesis wrote are sent regardless; this is added to them. A learner
   * who can name the thing that is absent is giving the search its single most useful steer,
   * and the alternative — inferring it from a curriculum that by definition does not contain
   * it — is the version of this feature that came back with the same document.
   */
  const [lookNote, setLookNote] = useState('');
  /* When it was asked for, which is what stops the flag above outliving it. */
  const [lookAskedAt, setLookAskedAt] = useState(0);
  /* When the fold was asked for, which is what stops the flag above outliving it. */
  const [foldAskedAt, setFoldAskedAt] = useState(0);
  /*
   * A fold finished while he was watching, which is when the next step has to be said
   * out loud.
   *
   * Held in this screen's memory rather than derived from the objective, because the
   * stored row cannot answer it: the two version stamps that would say "the questions
   * are older than the plan" are compared chronologically by `items.ts`, and a third
   * copy of that arithmetic on a screen is how three pipelines drifted apart before.
   * The cost is that a reload forgets — and what it forgets is a pointer to a panel
   * that is still on the same screen with its own button on it.
   */
  const [foldFinished, setFoldFinished] = useState(false);
  /*
   * Where generation had got to when the fold finished, so the count this screen shows
   * is this fold's and not the last one's. See the effect that sets it.
   */
  const [questionsBefore, setQuestionsBefore] = useState<string | undefined>();
  /*
   * A step has been asked for and the stored status has not caught up yet.
   *
   * This is what keeps the research button down. The API answers 202 the moment
   * the status is written, so the window between that answer and the screen
   * reading it back is exactly where a second press lands — and a second research
   * pass is the most expensive mistake this interface can make.
   */
  const [starting, setStarting] = useState(false);
  /*
   * The step's own complaint, kept apart from `problem` for the same reason the
   * history keeps its own: a refusal to start research reported next to the target
   * slider pins it on a control that had nothing to do with it.
   */
  const [stepProblem, setStepProblem] = useState<string | undefined>();
  /**
   * Why the research this objective was supposed to start on its own did not (F-141).
   *
   * **Separate from `stepProblem` because of when each is cleared, not because of where each is
   * shown** — they render in the same place. `stepProblem` is wiped by the effect keyed on
   * `status`, which is right for a refusal the learner provoked by pressing something: the press
   * moved the status, so the complaint about the state before it is stale. This one is set *by the
   * create*, before the screen has ever read a status, and the first read is what moves `status`
   * from `undefined` to `drafting` — so sharing the state meant the message was cleared by the
   * reload that was meant to display it, every time, on the path it was written for.
   *
   * It survives until the objective is genuinely past `drafting`, which is the moment it stops
   * being true.
   */
  const [createProblem, setCreateProblem] = useState<string | undefined>();
  const [waitedTooLong, setWaitedTooLong] = useState(false);
  /* Bumped to start another round of polling after this screen has given up. */
  const [pollRound, setPollRound] = useState(0);

  const load = useCallback(() => {
    /*
     * Which plan this read is for, so a late answer cannot overwrite a newer one.
     *
     * Tapping a tab changes the address without unmounting this screen, so two `load`s can be
     * in the air at once and the slower may be the older. Without this, plan A's curriculum,
     * research panels and close button would land under plan B's tab — the same fault
     * `Progress.tsx` guards twenty lines away and `Chat.tsx` calls "quieter than an
     * interleaved thread". A review caught that this screen had been given the dependency and
     * not the guard.
     */
    loadingFor.current = objectiveId;
    void currentObjective().then((result) => {
      if (loadingFor.current !== objectiveId) return;
      if (isError(result)) {
        setObjective(null);
        return;
      }
      /*
       * **The plan the address names, and the API's default only when it names nothing** —
       * F-151 step 8, and the same rule `Chat.tsx` has applied to its header since step 6.
       * Without it, tapping a tab would change the address, leave this screen showing the
       * default plan, and put one plan's curriculum, research and close button under another
       * plan's tab.
       *
       * Falls back when the named plan is not in the live list, which is a plan closed since
       * the link was made. `objectives` is absent from an API deployed before step 6, and an
       * absent list falls back too rather than showing nothing.
       */
      const named = result.objectives?.find((plan) => plan.objectiveId === objectiveId);
      const chosen = named ?? result.objective;
      setObjectives(result.objectives);
      setObjective(chosen);
      // The controls start from what is actually stored, so the slider reflects
      // the objective rather than a default that silently disagrees with it.
      if (chosen) {
        if (chosen.target.mode === 'score') {
          setMode('score');
          setScore(Math.round(chosen.target.targetScore * 100));
        } else {
          setMode('date');
          setDate(chosen.target.targetDate.slice(0, 10));
        }
        /*
         * Read back from the row, and `?? 'research'` is the same default the server applies.
         *
         * An objective created before F-118 carries nothing here, and leaving the control on
         * whatever the last render had would show a learner a radio that disagrees with what
         * their objective actually does — then save that disagreement the next time they touched
         * the panel for another reason.
         */
        setPlanFrom(chosen.planFrom ?? 'research');
      }

      /*
       * The curriculum is read here, from the status that came back with it,
       * rather than by whatever renders it. Asking separately would mean asking
       * before knowing whether this objective could have one — a 404 for every
       * draft on screen, which is noise that buries the real ones.
       */
      if (chosen && CURRICULUM_STATUSES.includes(chosen.status)) {
        void getCurriculum(chosen.objectiveId).then((curriculum) => {
          // An unreadable curriculum is shown as an absent one. There is nothing
          // different for the learner to do about either, and the alternative is
          // a complaint about a fetch beside a plan he can already read.
          setNodes(isError(curriculum) ? [] : curriculum.nodes);
        });
      } else {
        // Dropped rather than left standing: closing one objective and landing on
        // a draft would otherwise show the previous objective's plan beneath it. The coverage
        // report is dropped by its own effect below, which knows more about when it applies.
        setNodes(undefined);
      }

      /*
       * Where the research has got to, for every status and not only the three that wait.
       *
       * **The route built to be polled**, unlike the coverage report below it. `researchProgress`
       * is a `GetItem` on the objective and then a paginating `Query` over its rows; `coverageFor`
       * is that `GetItem` plus three more queries, which is why one of them rides the poll and the
       * other is read only when something that can move it has moved. This one rides this screen's
       * existing loop rather than adding a timer of its own, which is what keeps the four panels
       * that read it from disagreeing about the moment a subject settled.
       *
       * **The status gate is gone, and F-141 is why.** It used to name `researching`, `review`
       * and `synthesising`, on the reasoning that `drafting` has no run to report and a learner
       * at `active` is past this. Both halves stopped being true when research became a section
       * of this screen rather than a route: a `drafting` objective is where a learner lands after
       * a pass that *failed*, and its run row carries the twelve subjects and everything that was
       * read before it stopped; and an `active` learner three weeks in is exactly the person the
       * merge is for — *"asked about the vagus nerve, may well want to see what was actually read
       * about it"*. Withholding the query would have left both of them looking at a section that
       * said nothing had been read.
       *
       * The cost is one request, on arrival, for an objective that has never researched — and it
       * is the same request `Research.tsx` made unconditionally for the whole of its life as a
       * screen, on a route `Landing` sent every waiting learner to.
       *
       * An unreadable answer leaves the last good one standing. Clearing it would take the
       * counts and the graph off the screen for one failed request in the middle of a wait,
       * which is the failure this whole feature exists to stop.
       */
      const watched = chosen;
      if (section !== 'settings' && watched) {
        void getResearchProgress(watched.objectiveId).then((answer) => {
          if (isError(answer)) return;
          /*
           * No run is an answer and not a failure, so it clears. Closing one objective and
           * landing on a draft would otherwise leave the previous run's counts and graph under
           * it — and at `drafting` a stale run row is exactly what would make the panel claim
           * work was going on.
           */
          setProgress('run' in answer && answer.run ? (answer as ResearchProgress) : undefined);
        });
      } else if (section !== 'settings') {
        setProgress(undefined);
      }
    });

    /*
     * The closed ones come out of the full list, filtered here rather than asked
     * for separately. Which one is live stays the API's answer either way — this
     * screen only decides what counts as finished, and never promotes one of these
     * to live on its own reading.
     *
     * Asked for by the objective screen alone. It is the only one that renders the
     * history, and configuration would be spending a request, on a phone connection,
     * on a list nothing on its screen can show.
     */
    if (section === 'settings') return;
    void listObjectives().then((result) => {
      if (isError(result)) {
        setClosed([]);
        return;
      }
      setClosed(result.objectives.filter(isClosed).sort(byMostRecentlyClosed));
    });
  }, [section, objectiveId]);

  useEffect(load, [load]);

  const status = objective?.status;

  /**
   * The coverage report, on its own clock: whenever the research run's state moves.
   *
   * **It was folded into `load`, gated on a ref, and that was a bug the review caught before a
   * learner did.** The ref was written by an effect on `progress`, so it was always one `load`
   * behind: the poll that *observed* a run settling still read "still arriving" and skipped the
   * report, and the same reading then took `waiting` down and tore the loop out. Nothing asked
   * again. A learner paid for a deeper pass on a barren subject, watched it settle, and the row
   * went on saying nothing was found until they reloaded — this project's signature failure,
   * arriving one render late through a ref.
   *
   * Keyed on `progress?.run.state` instead, which is what `Research.tsx` did while it owned this
   * request and is the honest dependency — the report moves when a run settles or a plan is built,
   * and this is the first of those. It is still deliberately off the polling path: `coverageFor`
   * is four reads, and a state that does not change costs nothing here because the effect does not
   * re-run.
   *
   * **And not only where there is a plan to compare against.** `review` and `active` are where the
   * comparison means something, and a run row on any other status is where the *verdicts* still
   * do: a `drafting` objective whose pass failed has twelve subjects, each with an outcome derived
   * from its state, each carrying the two remedies F-129 promised. Gating on the status alone
   * would have taken the "add a page" button off exactly those rows.
   *
   * An unreadable report leaves the panel as it was rather than emptying it — every subject keeps
   * its row, its state and its bar, and the only thing lost is the sentence about the plan.
   */
  useEffect(() => {
    if (section === 'settings') return;
    const objectiveId = objective?.objectiveId;
    const runState = progress?.run.state;
    if (!objectiveId || !status) {
      setCoverage(undefined);
      return;
    }
    if (!CURRICULUM_STATUSES.includes(status) && !runState) {
      // Nothing to report on: no plan to compare against and no run to have verdicts from.
      setCoverage(undefined);
      return;
    }
    /*
     * While material is still arriving there is nothing settled to join, and F-146 made
     * `review`-with-subjects-still-coming a polling state — so this is what keeps four reads off
     * the poll. `stillComing` and not `researchArriving`: the guard and the dependency below have
     * to be the same value, or a run whose row goes stale without its state moving would never
     * take the gate off. The staleness window exists to give a learner their *build button* back,
     * which is a different question from what a read costs.
     */
    if (stillComing(runState)) return;

    let stale = false;
    void getCoverage(objectiveId).then((report) => {
      // The objective may have changed while this was in flight; landing the previous one's
      // report on this one's subjects is the cross-objective leak every fetch here guards against.
      if (stale) return;
      setCoverage(isError(report) ? undefined : report);
    });
    return () => {
      stale = true;
    };
  }, [section, objective?.objectiveId, status, progress?.run.state]);

  /*
   * Whether a pass is folding material into the plan right now.
   *
   * Read from the objective's own `synthesising` attribute rather than from its
   * status, because a live pass leaves the status at `active` on purpose — that is
   * the whole of F-87's safety, and it means the status has nothing to say about
   * this. `folding` covers the gap before storage catches up: the API answers the
   * moment the attribute is written, but this screen reads the objective back
   * through a secondary index that is eventually consistent, so for a moment after
   * the answer the row still comes back without it.
   */
  const foldingNow = folding || objective?.synthesising === true;

  /*
   * Whether a research pass is running on this objective right now.
   *
   * Read from the `researching` *attribute* rather than from the status, because a
   * second pass leaves the status where it found it — at `active` so the sweep goes
   * on finding the learner, and at `review` so a failure cannot strand the
   * curriculum on `drafting`. The status therefore has nothing to say about this.
   *
   * The API takes the attribute off the row once it is older than any pass could
   * be, so this cannot be a spinner that outlives the work by more than the window
   * the button is locked for anyway.
   */
  const lookingNow = looking || objective?.researching === true;

  /*
   * Everything about a step in flight is cleared when the *status* changes, not
   * when the request returns.
   *
   * `starting` has to outlive its own request, or the button comes back for the
   * length of a reload. It must not outlive the status, though: research that
   * fails lands back on `drafting`, and a flag left set there would leave the
   * retry button dead for the rest of the session with no way to press it.
   */
  useEffect(() => {
    setStarting(false);
    setWaitedTooLong(false);
    setStepProblem(undefined);
    /*
     * `createProblem` is deliberately not cleared here, and is cleared below instead.
     *
     * Every other piece of state in this effect is about a press that has just moved the status,
     * so any status change makes it stale. The create-time refusal is about the status *being*
     * `drafting` — the first read is what sets it there — so clearing it on any change would clear
     * it on arrival.
     */
  }, [status]);

  /* The create-time refusal stops being true the moment the objective leaves `drafting`, which is
     the moment a pass has actually started. Until then the plan screen offers the retry, and this
     is the sentence explaining why it is being offered. */
  useEffect(() => {
    if (status !== undefined && status !== 'drafting') setCreateProblem(undefined);
  }, [status]);

  /*
   * Handing the fold's in-flight state over to storage, and never holding it longer.
   *
   * The flag exists only to cover the gap between the API answering and the row
   * showing a lock, because this screen reads the objective back through an
   * eventually consistent index. Once the lock is visible the row is a better
   * witness than the flag, so the flag goes.
   *
   * **The elapsed check is the part that is not decoration.** Waiting for the lock
   * to appear and then disappear looks equivalent and strands the button when it
   * never appears at all: a pass that fails in three seconds — no material to build
   * from is the easy way — sets the lock and clears it entirely between two polls,
   * and a flag waiting for an edge it already missed disables the only button that
   * would try again, for the rest of the session, with nothing on screen saying so.
   * A poll interval after the request, an unlocked row means the pass is over or
   * never started, and either way the button belongs back. A second press then costs
   * nothing: the lock lives in DynamoDB, and the API refuses one.
   */
  useEffect(() => {
    if (!folding) return;
    if (objective?.synthesising) {
      setFolding(false);
      return;
    }
    if (Date.now() - foldAskedAt > POLL_INTERVAL_MS) setFolding(false);
  }, [objective, folding, foldAskedAt]);

  /*
   * The same handover for the research lock, and the same elapsed check.
   *
   * Waiting for the attribute to appear and then disappear looks equivalent and
   * strands the button when it never appears at all — a pass refused before it
   * started sets nothing. A poll interval after the request, an unlocked row means
   * the claim did not stick, and a second press then costs nothing because the lock
   * lives in DynamoDB and the API refuses one.
   */
  useEffect(() => {
    if (!looking) return;
    if (objective?.researching) {
      setLooking(false);
      return;
    }
    if (Date.now() - lookAskedAt > POLL_INTERVAL_MS) setLooking(false);
  }, [objective, looking, lookAskedAt]);

  /*
   * A pass that was running and is not any more.
   *
   * Watched on the stored attribute rather than on the local flag, because that is
   * the one that only clears when the pipeline says the pass is over. It is the
   * moment — and the only moment — at which this screen can be sure the plan has
   * changed underneath a learner who is still being taught from it.
   */
  const wasFolding = useRef(false);
  useEffect(() => {
    if (wasFolding.current && !objective?.synthesising) {
      setFoldFinished(true);
      /*
       * What generation had last written *before* this fold's questions were started.
       *
       * **Without this the screen reports the previous run's count as this one's.** A
       * fold does not touch `itemsWritten`, so the instant it finishes the row still
       * carries whatever the last generation wrote — "47 new questions" about a run
       * from last week, shown next to three topics that have none. The baseline is
       * this screen's own memory of where generation had got to, and `itemsAt` moving
       * off it is what says the run the learner is waiting on has reported.
       *
       * Captured here rather than derived, and deliberately not derived from the two
       * version stamps: comparing them is arithmetic `items.ts` owns, and a third copy
       * of it on a screen is how three pipelines drifted apart before.
       */
      setQuestionsBefore(objective?.itemsAt ?? '');
    }
    wasFolding.current = objective?.synthesising === true;
  }, [objective?.synthesising, objective?.itemsAt]);

  /*
   * How much this fold changed, as the numbers the pipeline wrote rather than the
   * sentence it wrote. See `addedNodes` in api.ts.
   */
  const foldAdded = objective?.addedNodes ?? 0;
  const foldChanged = objective?.changedNodes ?? 0;
  const foldTouched = foldAdded + foldChanged;

  /*
   * Whether the questions for what this fold added have been written yet.
   *
   * Two conditions, and both are load-bearing. `itemsWritten` is present only when the
   * last thing generation wrote was a *finished* run — it is removed on a progress
   * write and on a failure, which is what stops a batch that has merely been submitted
   * reading as a completed one. And `itemsAt` has to have moved off the baseline above,
   * or the answer is the previous run's.
   */
  const questionsWritten =
    objective?.itemsWritten !== undefined && objective.itemsAt !== questionsBefore;

  /*
   * The two states the chain has after a fold, and they are not the same sentence.
   *
   * A fold that added or reworded something has started generation on its own and the
   * learner is owed "we are writing them"; one that changed nothing has started
   * nothing and is finished already. Saying "writing the questions" about a fold that
   * will never write any is the spinner that never resolves.
   */
  const writingQuestions = foldFinished && !foldingNow && foldTouched > 0 && !questionsWritten;
  const foldSettled =
    foldFinished && !foldingNow && (foldTouched === 0 || questionsWritten);

  /*
   * Waiting on something that takes minutes.
   *
   * `starting` counts as waiting alongside the two statuses because the read that
   * would confirm them goes through a secondary index and is eventually
   * consistent: for a moment after the 202 the objective still comes back as it
   * was, and a poll that trusted only the status would not have started yet.
   */
  /*
   * Configuration does not watch anything.
   *
   * Polling is what turns "a pass is running" into a screen that updates itself, and one of the
   * two sections renders passes: the objective screen shows the step it is at, the subjects as
   * they land, the fold and the re-research. The target slider shows none of them, so a learner
   * who left the app open on it would otherwise spend a request every fifteen seconds, for
   * twenty minutes, on mobile data, to redraw a number that cannot change while they are looking
   * at it.
   */
  const watching = section !== 'settings';

  const waiting =
    watching &&
    (starting ||
      status === 'researching' ||
      status === 'synthesising' ||
      foldingNow ||
      lookingNow ||
      /*
       * Research still arriving on an objective whose status has already moved (F-146).
       *
       * **Without this the panel that F-146 added would be a screen that never updates.** An
       * early fold leaves the objective at `review` while the remaining subjects are still being
       * read, and none of the conditions above is true there — the status says `review`, no lock
       * is held, nothing is being folded. The learner would be shown "reading up on it", which is
       * the truth, and then left looking at it until they reloaded, which is the complaint this
       * whole feature exists to answer.
       */
      researchArriving(progress?.run, Date.now()) ||
    /*
     * Generation counts as waiting now that nobody starts it.
     *
     * It never moves the status, so before F-92 there was nothing to poll for: the
     * learner had pressed a button and could read the questions panel whenever they
     * liked. Now the fold starts it, and a screen that stopped polling when the fold
     * finished would sit on "writing them" until it was reloaded — the plan changed
     * under a learner who was never told the rest happened.
     */
      writingQuestions);

  useEffect(() => {
    if (!waiting) return;
    let stopped = false;

    /*
     * Polling, rather than pushing, for one event the learner is already sitting
     * in front of. There is no stop condition in the loop: a status that has
     * moved on takes `waiting` down with it, which tears this effect down, which
     * is the same mechanism as leaving the screen — one way for this to stop
     * rather than two that can disagree.
     */
    void (async () => {
      const startedAt = Date.now();
      let window = POLL_WINDOW_MS;
      while (Date.now() - startedAt < window) {
        await new Promise((resolve) => setTimeout(resolve, pollEvery.current));
        if (stopped) return;
        load();
        // `Math.max`, so the window grows when a run starts and never shrinks when one ends.
        window = Math.max(window, pollWindow.current);
      }
      if (!stopped) setWaitedTooLong(true);
    })();

    // Without this the loop outlives the objective it was watching, and somebody
    // who closes one and states another gets two loops reloading the screen from
    // underneath him.
    return () => {
      stopped = true;
    };
  }, [waiting, load, pollRound]);

  const target = (): Objective['target'] =>
    mode === 'score'
      ? { mode: 'score', targetScore: score }
      : { mode: 'date', targetDate: date };

  function explain(error: string): string {
    if (error === 'invalid_target') return t('obj.error.target');
    if (error === 'description_required') return t('obj.error.description');
    if (error === 'sign_in_required') return t('obj.error.signIn');
    if (error === 'already_active') return t('obj.error.alreadyActive');
    if (error === 'not_found') return t('obj.error.notFound');
    if (error === 'not_closed') return t('obj.error.notClosed');
    if (error === 'conflict') return t('obj.error.conflict');
    if (error === 'not_ready') return t('items.error.notReady');
    if (error === 'already_running') return t('items.error.alreadyRunning');
    // Not an error: the plan builds itself once enough research has landed.
    if (error === 'still_researching') return t('plan.error.stillResearching');
    if (error === 'nothing_active') return t('start.error.nothingActive');
    if (error === 'no_items') return t('start.error.noItems');
    if (error === 'not_drafting') return t('obj.error.notDrafting');
    if (error === 'not_ready') return t('obj.error.notReady');
    if (error === 'no_material') return t('obj.error.noMaterial');
    /*
     * Both belong to the plan source (F-118), and both say what happened rather than "something
     * went wrong". `no_research` is reachable only from a stale screen — the research button is
     * not rendered for an uploads-only objective at all — and a learner reading it needs to know
     * that the refusal is their own setting and where to change it, or they will press again.
     */
    if (error === 'no_research') return t('obj.error.noResearch');
    // F-119: the scoped objective's own refusal, which is not "you opted out of research" but
    // "your research has not been given anything to be about yet".
    if (error === 'awaiting_material') return t('obj.error.awaitingMaterial');
    if (error === 'busy') return t('obj.error.busy');
    /*
     * F-151: the month's research grant is spent. **A fact, not a failure**, and it is here
     * because without it this falls through to `obj.error.generic` — "something went wrong" —
     * which is both false and the one message that makes somebody press the button again.
     *
     * Reachable from all three research controls, because the grant counts runs rather than
     * objectives: the first pass, "look again", and "go deeper on this subject" each spend one.
     */
    if (error === 'research_grant_spent') return t('obj.error.researchGrantSpent');
    return t('obj.error.generic');
  }

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);

    const result = await createObjective(description, target(), planFrom);
    setBusy(false);
    if (isError(result)) {
      setProblem(explain(result.error));
      return;
    }
    /*
     * The research pass has already been asked for by the time this returns (F-141).
     *
     * Leo: *"the user should not have to click on anything for this to happen, if I told it what i
     * want and that I want it researched, it should just do it all on its own."* So there is no
     * button to press here and the objective comes back already `researching` — but the pass can
     * still be refused, by a spent monthly grant above all, and a screen that said nothing would
     * leave somebody watching a wait that never started. `research` is the API's own word for
     * which of the two happened.
     *
     * **Tested positively against `not_started` and not negatively against `started`**, because
     * the third answer is `not_applicable` — an uploads-only objective, which is fine — and the
     * fourth is the field being absent altogether, which is an answer from an API older than this
     * change. A negative test would put an error message under both of those.
     */
    if (result.research === 'not_started') {
      /*
       * Its own state, and **not `stepProblem`**, which is what this was and what made it
       * invisible. `stepProblem` is cleared by the effect keyed on `status` — and `load()` two
       * lines below is precisely what moves the status from `undefined` to `drafting`, so the
       * message was set and then wiped by the reload that was meant to reveal it. The learner
       * whose month's research was spent saw a plan screen with a button and no explanation.
       */
      setCreateProblem(explain(result.reason ?? 'generic'));
    }
    /*
     * Straight to configuration, which is step 2 of the design and the whole of the first run.
     *
     * **Navigated rather than left to the gate, and the gate is still what makes it hold.** They
     * are two different mechanisms for two different cases. This is the learner who has just
     * pressed the button, and they are moved *now*: `FirstRunGate` decides from a read of
     * `GET /api/objectives/current`, which answers through an eventually consistent index, so an
     * objective created a moment ago may not be in it yet — waiting for the gate would leave
     * somebody on a plan screen for a poll or two before it moved them, which reads as the app
     * changing its mind. The gate covers the other case: the deep link, the restored tab, the
     * notification tap, and this navigation not happening at all.
     *
     * `replace`, so the back gesture does not land on a form that immediately re-submits nothing.
     *
     * **A refusal set two lines above is lost when this navigates**, and that is acceptable rather
     * than overlooked: the message is transient state on a screen the learner is leaving, and the
     * durable half is the row. A pass that did not start leaves the objective on `drafting`, so
     * this screen offers *start research* when they come back to it — and pressing it puts the
     * same sentence in front of them, from the same `explain`, against a live reading rather than
     * a remembered one. Carrying it across the navigation would mean holding an error about one
     * screen in the state of another, which this file has three separate comments about already.
     *
     * Only when the window is known to be missing. `undefined` facts are a read that failed or has
     * not landed, and sending somebody to the welcome screen on a guess would show the
     * configuration step to a learner who configured months ago — `Welcome` would bounce them
     * straight back, which is the double decision this whole feature is careful about.
     */
    /*
     * **Told, not asked.** `reread` was here and only *started* a fetch, so the navigation below
     * ran in the same tick and `/welcome` mounted against pre-create facts — `status: 'none'`,
     * which `firstRun` answers `open` for, which sends the learner straight back to `/plan`. The
     * gate then pushed them forward again a round trip later, or never: `currentObjective` reads
     * through `gsi3` and nothing polls, so a single re-read landing inside the index's consistency
     * window left somebody on `/plan` with no reach window for the rest of the session — F-140's
     * sequence exactly, with the banner that used to announce it now deleted.
     *
     * The status in hand came from the create route, which wrote it. Nothing is more authoritative
     * than that, and it is available now rather than a request later.
     */
    noteFirstRun({ status: result.objective.status });
    if (firstRunFacts && !firstRunFacts.windowConfigured) {
      navigate(ROUTES.welcome, { replace: true });
      return;
    }
    // Not cleared here: `load` replaces the whole screen with the objective, so
    // there is no stale form left holding an empty description.
    load();
  }

  async function saveTarget() {
    if (!objective) return;
    setBusy(true);
    setProblem(undefined);
    setSaved(false);

    const result = await updateTarget(objective.objectiveId, target());
    setBusy(false);
    if (isError(result)) {
      setProblem(explain(result.error));
      return;
    }
    setSaved(true);
    load();
  }

  /**
   * Saves the choice of where the material comes from, and starts nothing.
   *
   * **Switching to "find it for me" does not run a research pass**, and that is deliberate
   * rather than unfinished: the plan screen offers the pass when the learner is ready, and a
   * setting that spent the most expensive call in the system on being saved is the button this
   * app has always declined to build. Switching the other way stops nothing that is already
   * running either — the API refuses the change outright while a pass is in flight, which is
   * what `busy` is — because a pass killed halfway is money spent for nothing.
   *
   * Its own message rather than the target's, for `startProblem`'s reason: one piece of error
   * state read in two panels is the same complaint pinned to a control that had nothing to do
   * with it, which is how it reached Leo as "the error came up in multiple places".
   */
  async function savePlanSource() {
    if (!objective) return;
    setBusy(true);
    setProblem(undefined);
    setSourceSaved(false);

    const result = await updatePlanSource(objective.objectiveId, planFrom);
    setBusy(false);
    if (isError(result)) {
      setProblem(explain(result.error));
      // The screen's reading of the row is behind storage in both refusals — something started
      // running, or the objective moved — and reloading is what answers either.
      load();
      return;
    }
    setSourceSaved(true);
    load();
  }

  /**
   * Starts the research pass, once.
   *
   * `starting` is set before the request and deliberately not cleared when it
   * returns: the effect above clears it when the stored status moves, so there is
   * no instant at which an enabled button sits in front of a learner whose
   * research has already been paid for. Everything else in this file can afford a
   * double submit; this cannot.
   */
  async function research() {
    if (!objective) return;
    setStarting(true);
    setStepProblem(undefined);
    setWaitedTooLong(false);

    const result = await startResearch(objective.objectiveId);
    if (isError(result)) {
      setStarting(false);
      setStepProblem(explain(result.error));
      // Refused on the grounds of status, which means this screen was drawing
      // from a stale reading of it. Reloading answers that; retrying would put
      // the same question to the same stale reading.
      if (result.error === 'not_drafting') load();
      return;
    }
    load();
  }

  /** Builds the curriculum from whatever has been read so far. Valid from `review`. */
  async function synthesise() {
    if (!objective) return;
    setStarting(true);
    setStepProblem(undefined);
    setWaitedTooLong(false);

    const result = await startSynthesis(objective.objectiveId);
    if (isError(result)) {
      setStarting(false);
      setStepProblem(explain(result.error));
      // `no_material` is not stale — the objective really is in `review` with
      // nothing to build from — so the message stands and the screen is left
      // alone, with the offer to add a page of his own still under it.
      if (result.error === 'not_ready') load();
      return;
    }
    load();
  }

  /**
   * Folds everything added since the plan was built into the plan.
   *
   * The same endpoint as the rebuild below, and deliberately a different act on the
   * screen: from `review` it replaces a curriculum nobody has studied yet, and from
   * `active` it extends one somebody is halfway through. Only the second one has a
   * learner's review history to lose, which is why it is written about, refused and
   * reported separately even though one request serves both.
   *
   * The answer's `status` is ignored — see `startSynthesis` in api.ts. What the
   * objective is doing is read back off the objective.
   */
  async function fold() {
    if (!objective) return;
    setFolding(true);
    setFoldAskedAt(Date.now());
    setFoldFinished(false);
    setFoldProblem(undefined);
    setWaitedTooLong(false);

    const result = await startSynthesis(objective.objectiveId);
    if (isError(result)) {
      setFolding(false);
      setFoldProblem(explainFold(result.error));
      // Both refusals mean the screen is arguing with a row it has not read
      // recently: one says a pass is already running, the other that the objective
      // has moved somewhere a pass cannot start from. Reloading answers both, and
      // retrying against the same stale reading would not.
      load();
      return;
    }
    load();
  }

  /**
   * Asks research to go looking again, once, and only after he has agreed to the
   * bill.
   *
   * **The confirmation names the figure.** Every other dialog on this screen exists
   * to stop somebody undoing work by accident; this one exists to stop them
   * spending money by accident, and a dialog that says "are you sure?" without the
   * number is not informed consent, it is a speed bump. The conditional write
   * behind the endpoint is what makes a *second* press free — the dialog is what
   * makes the first one deliberate.
   *
   * Nothing about the plan changes here. The pass adds a document to the material
   * this objective was built from; folding it in is a separate act, further down,
   * which is what keeps this from being a button that quietly rewrites a curriculum
   * somebody is halfway through.
   */
  async function lookAgain() {
    if (!objective) return;
    if (!window.confirm(t('again.confirm'))) return;

    setLooking(true);
    setLookAskedAt(Date.now());
    setLookProblem(undefined);
    setWaitedTooLong(false);

    const result = await startReresearch(objective.objectiveId, lookNote);
    if (isError(result)) {
      setLooking(false);
      setLookProblem(explainLook(result.error));
      // Both refusals mean this screen is arguing with a row it has not read
      // recently: one says a pass is already running, the other that the objective
      // is not somewhere one can start. Reloading answers both; retrying against
      // the same stale reading would not.
      load();
      return;
    }
    load();
  }

  /**
   * The re-research's own refusals, in its own words.
   *
   * `explain` answers `not_ready` with "build the plan above first", which is
   * written for the questions panel and is nonsense next to a plan that exists and
   * is being taught — the same reason `explainFold` exists.
   */
  function explainLook(error: string): string {
    if (error === 'already_running') return t('again.error.running');
    if (error === 'not_ready') return t('again.error.notReady');
    return explain(error);
  }

  /**
   * The fold's own refusals, in its own words.
   *
   * `explain` answers `not_ready` with "build the plan above first", which is
   * written for the questions panel and is nonsense next to a plan that already
   * exists and is being taught.
   */
  function explainFold(error: string): string {
    if (error === 'already_running') return t('fold.error.running');
    if (error === 'not_ready') return t('fold.error.notReady');
    return explain(error);
  }

  /** Another round of polling, after this screen has stopped watching. */
  function checkAgain() {
    setWaitedTooLong(false);
    setPollRound((round) => round + 1);
    load();
  }

  /**
   * Brings a closed objective back, setting the live one aside if there is one.
   *
   * The confirmation is asked for only when something would actually be displaced.
   * A dialog on the other path would be asking him to agree to a consequence that
   * does not exist, which is how people learn to dismiss dialogs unread.
   */
  async function reactivate(target: Objective) {
    if (objective && !window.confirm(t('obj.history.confirm'))) return;

    setBusy(true);
    setClosedProblem(undefined);
    setClosedNote(undefined);

    const result = await reactivateObjective(target.objectiveId);
    setBusy(false);
    if (isError(result)) {
      setClosedProblem(explain(result.error));
      if (STALE_ERRORS.includes(result.error)) load();
      return;
    }

    /*
     * Whether anything was set aside is the server's answer, not a guess from what
     * this screen thought was live a moment ago.
     *
     * `.length` and not truthiness: `shelved` is a list since F-151 step 6, because the cap
     * is the tier's and a learner can have more than one plan put away at once. The API
     * omits the field when nothing moved, so the length test is belt and braces — and it is
     * the half that would survive somebody deciding to send `[]` one day.
     */
    const shelved = result.shelved ?? [];
    /*
     * Four answers rather than two, and the two new ones are the expensive moments.
     *
     * `shelvedMidRun` is the subset of `shelved` that had a pass actually running (F-151).
     * Saying only "set aside in this list" about a plan whose progress bar has just vanished
     * leaves somebody to conclude that six dollars of research went with it; nothing is
     * cancelled, so the screen says so. `.length` rather than truthiness for the reason
     * above: the API omits these fields when they are empty.
     *
     * **Research and a plan build get different sentences, and the difference is a promise
     * only one of them can keep.** A research pass ends in `research_ready`, which is sent
     * even though the plan was shelved; a build ends in no notification at all, because
     * there is no such event. So the build copy says where the result will be instead of
     * saying somebody will be told. A review caught the single message promising both.
     *
     * Research wins a mixed answer, which needs a cap of three to happen: it is the one with
     * money and a notification attached, so it is the one worth describing.
     */
    const midRun = result.shelvedMidRun ?? [];
    const midRunNote = midRun.some((plan) => plan.pass === 'research')
      ? 'obj.history.switchedMidRun'
      : 'obj.history.switchedMidBuild';
    setClosedNote(
      midRun.length > 0
        ? t(midRunNote)
        : shelved.length > 0
          ? t('obj.history.switched')
          : t('obj.history.resumed'),
    );
    // Held open past the switch so that message is read where it happened.
    setClosedOpen(true);
    // Cleared for the same reason `create` relies on: the description survives in
    // state while the objective screen covers the form, and reappears in it the
    // next time something is closed — text he typed weeks ago, presented as a draft.
    setDescription('');
    /*
     * **The resume names its own plan in the address, and that is F-151's third owed
     * decision answered without a new field** (see `docs/backlog.md`).
     *
     * The default plan is "most recently asked", keyed on `lastAskedAt`, which the sweep's
     * claim writes. A resume is the clearest statement of intent the product has and it
     * writes nothing that key can see — so at a raised cap, bringing plan B back while plan
     * A was asked yesterday left this screen on A, one press after the learner said B. What
     * it needed was not a second stamp competing with `lastAskedAt` at every reader, but the
     * thing step 8 already minted: `?o=` is where "which plan do you mean" is decided, so the
     * resume says it there. Stamping `lastAskedAt` instead would have been actively wrong —
     * it is the sweep's rotation key, and moving it forward makes the resumed plan the *last*
     * one asked next, which is the opposite of what pressing this asks for.
     *
     * `replace` and not a push: the address this leaves behind names a plan that was live a
     * moment ago and has just been shelved, and a back button is not a way to un-shelve it.
     *
     * The reload is the address's, not this function's — `load` is keyed on `objectiveId`, so
     * setting it re-reads for the new plan. `load()` is called directly only when the address
     * already named this plan and nothing would change, which happens when a learner resumes
     * the plan they were already looking at.
     */
    if (objectiveId === target.objectiveId) {
      load();
      return;
    }
    const next = new URLSearchParams(search);
    next.set('o', target.objectiveId);
    setSearch(next, { replace: true });
  }

  async function close(status: 'done' | 'abandoned') {
    if (!objective) return;
    if (!window.confirm(t('obj.close.confirm'))) return;

    setBusy(true);
    setProblem(undefined);
    const result = await closeObjective(objective.objectiveId, status);
    setBusy(false);
    if (isError(result)) {
      setProblem(explain(result.error));
      return;
    }
    setDescription('');
    setScore(85);
    setDate('');
    setMode('score');
    // Back to the default too, or the next objective silently inherits the last one's choice —
    // which for somebody who photographed a textbook once would mean every objective after it
    // waits for pages they did not mean to promise.
    setPlanFrom('research');
    load();
  }

  /**
   * The bar of plans, on this screen's three faces and on none of configuration's. F-151 step 8.
   *
   * Assembled once and rendered into each branch rather than wrapped around the component,
   * because this screen returns from several places — while the plan is loading, when there is
   * no plan yet, and for the plan itself, with configuration's own two branches beside them —
   * and a bar that lived in only the last of them would disappear at exactly the moments a
   * learner is most likely to want their other plan. It renders nothing at all until there are
   * two plans; see `objectiveTabs`.
   */
  const tabs =
    section === 'plan' ? (
      <ObjectiveTabs
        objectives={objectives}
        selected={objectiveId ?? objective?.objectiveId}
        path={ROUTES.plan}
      />
    ) : null;

  if (objective === undefined) {
    return (
      <>
        {tabs}
        <section className="panel">
          <p className="muted">{t('obj.list.loading')}</p>
        </section>
      </>
    );
  }

  /*
   * The live objective is kept out of its own history by id, not only by status.
   * The list is read from a secondary index and is eventually consistent, so for a
   * moment after a switch the objective he just came back to can still be reported
   * as shelved — offering "pick this up again" on the objective already on screen.
   */
  const history = (
    <ClosedObjectives
      items={closed.filter((o) => o.objectiveId !== objective?.objectiveId)}
      live={objective}
      busy={busy}
      note={closedNote}
      problem={closedProblem}
      // Expanded by default only when nothing is live, because then this list is
      // half of what the screen is for; collapsed under a live objective, which is
      // already a long scroll on a phone. Either way his own last choice wins.
      open={closedOpen ?? !objective}
      onOpenChange={setClosedOpen}
      onReactivate={(target) => void reactivate(target)}
    />
  );

  // ---- No live objective: ask for one ----

  if (objective === null) {
    /*
     * Configuration acts on an objective, and there is not one.
     *
     * It says so and points at the screen that fixes it, rather than rendering its own
     * controls at nothing: a target slider that saves to no objective is a button whose only
     * possible outcome is an error explaining what is missing. The research half used to be
     * here too and is now part of the screen below, which asks for the objective directly.
     */
    if (section === 'settings') return <NoObjective />;

    return (
      <>
        {tabs}
        <Disclosure id="new" title={t('obj.new.title')} repeatsTitle>
          <section className="panel">
            <h2>{t('obj.new.title')}</h2>
            <form onSubmit={create} className="form">
              <label className="field">
                <span>{t('obj.description.label')}</span>
                <textarea
                  rows={7}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('obj.description.placeholder')}
                  maxLength={4000}
                  disabled={busy}
                  required
                />
                <small>{t('obj.description.hint')}</small>
              </label>

              {/*
               * Asked here, on the same argument the target is asked here (F-118).
               *
               * Where the material comes from decides what happens the moment this form is
               * saved — a research objective's next step is a button, an uploads-only one's is
               * the camera — so asking it afterwards would mean the screen that follows is
               * built on a guess. Changing it a week later is a setting, and the same component
               * renders there.
               */}
              <PlanSourceFields
                planFrom={planFrom}
                setPlanFrom={setPlanFrom}
                disabled={busy}
              />

              {/*
               * The target is asked for here and adjusted under configuration, and that
               * is not the split leaking. Stating an objective without saying what "done"
               * means would send somebody to a settings screen to finish a sentence they
               * started here; changing it a month later is a setting. Same control, one
               * component, two moments.
               */}
              <TargetFields
                mode={mode}
                setMode={setMode}
                score={score}
                setScore={setScore}
                date={date}
                setDate={setDate}
                disabled={busy}
              />

              {problem && (
                <p className="problem" role="alert">
                  {problem}
                </p>
              )}

              <div className="actions">
                <button type="submit" className="button primary" disabled={busy}>
                  {busy ? t('obj.working') : t('obj.new.submit')}
                </button>
              </div>
            </form>
          </section>
        </Disclosure>
        {history}
      </>
    );
  }

  // ---- A live objective: it is the screen ----

  const title = titleOf(objective, locale, t('obj.history.untitled'));

  /*
   * Which of the panels below this objective is actually at, decided in one place.
   *
   * `planStep` is imported from a DOM-free module so the decision is asserted in a test rather
   * than reasoned about here — see `plan/step.ts`. The four booleans stay because the JSX reads
   * on them, but every one of them now comes from that single answer instead of from four
   * independent status comparisons that could all be true at once.
   *
   * **The third argument is what this screen knows and the stored status does not** (F-144,
   * F-146). `starting` is the press, in the window before an eventually consistent read agrees it
   * happened; `stillComing` is the run row saying material is still arriving for an objective
   * whose status already reads `review` — and saying it recently enough to be believed. Both only ever withdraw an offer — neither can invent
   * one — so a progress read that fails or has not landed leaves the screen exactly as it was.
   */
  const nextMove = planStep(objective, nodes?.length, {
    starting,
    stillComing: researchArriving(progress?.run, Date.now()),
  });
  const drafting = nextMove === 'research';
  /*
   * The uploads-only objective with nothing read yet, which is the state Leo named: *"they dont
   * need research just photos of the pages in the books"*. Nothing is happening and nothing is
   * supposed to be — so this is an invitation with one button, never a spinner and never a
   * complaint about an objective that is perfectly fine.
   */
  const awaitingMaterial = nextMove === 'add_material';
  const researching = nextMove === 'researching';
  const building = nextMove === 'building';
  const canBuild = nextMove === 'build';
  /*
   * Whether the step panel is already reporting a wait, which decides who may offer the escape.
   *
   * **Both panels are on one screen now** (F-141), and the comment on the *look again* panel used
   * to say that could not happen — "safe to share `waitedTooLong`… those render inside the step
   * panel, which needs a status this branch cannot have" was true of two routes and is not true
   * of one page. At `review` with no plan and a second look running, `planStep` answers
   * `researching` *and* the objective carries the research lock, so both panels would say "this
   * is taking a while" with an identical button under it. They start the same poll; the higher
   * one is the one the learner is looking at.
   */
  const stepIsWaiting = researching || building;
  /*
   * The findings, as the numbers a learner is owed before being asked to build on them.
   *
   * Computed from the same progress reading the panel above renders, so the sentence and the
   * graph under it cannot disagree — and absent rather than zeroed when the run has not been
   * read, because "0 of 0 subjects researched" under an offer to build a curriculum is a worse
   * answer than the silence this replaced.
   */
  const findings = progress ? pulseOf(progress.subjects) : undefined;
  /*
   * Whether this screen may show anything that spends on research.
   *
   * The API refuses all of it anyway and the pipeline refuses it again, so this is not the guard
   * — it is the reason the learner never has to read a refusal. Offering a button whose only
   * outcome is "you chose not to do this" is the interface arguing with a setting it rendered.
   */
  const researchOffered = offersResearch(objective);

  /**
   * Asks for the questions to be written.
   *
   * There is nothing to poll afterwards. Generation leaves the status where it is,
   * so the screen cannot watch a status change the way it watches research — and it
   * should not poll for minutes to hours either. The note says it is happening and
   * that closing the page is safe; `itemVersion` on the next natural load is what
   * confirms it landed.
   */
  async function writeItems() {
    if (!objective) return;
    setWritingItems(true);
    setItemsProblem(undefined);
    setItemsNote(undefined);

    const result = await startItemGeneration(objective.objectiveId);
    setWritingItems(false);
    if (isError(result)) {
      setItemsProblem(explain(result.error));
      // `not_ready` means this screen's idea of the curriculum is behind storage.
      if (result.error === 'not_ready') load();
      return;
    }
    setItemsNote(t('items.started'));
  }

  /**
   * Starts the loop.
   *
   * Confirmed, unlike every other action on this screen, because it is the only one
   * whose consequence lands somewhere other than this page — on a phone, repeatedly,
   * for weeks. That is worth one dialog.
   */
  async function start() {
    if (!objective) return;
    if (!window.confirm(t('start.confirm'))) return;

    setStarting_(true);
    setStartProblem(undefined);
    const result = await activateObjective(objective.objectiveId);
    setStarting_(false);
    if (isError(result)) {
      setStartProblem(explain(result.error));
      return;
    }
    // Reloaded rather than patched locally: activation changes the status, which
    // decides what this whole screen renders.
    load();
  }

  /*
   * The next step, in one panel, and only the step he is actually at.
   *
   * The lifecycle is a line — draft, research, review, running — and every status
   * has exactly one thing to do next. Showing the others greyed out would put two
   * steps he cannot take beside the one he can, and the greyed-out research
   * button in particular would invite the press this whole flow is built to
   * prevent. So each status renders its own step and nothing else.
   */
  const step = (
    drafting ||
    awaitingMaterial ||
    researching ||
    building ||
    canBuild ||
    stepProblem ||
    createProblem
  ) && (
    <section className="panel">
      {/*
       * The uploads-only objective waiting for its first page.
       *
       * Rendered *instead of* the research offer and never beside it: `planStep` returns one
       * value, so the two cannot both be true. That exclusivity is the point — a greyed-out or
       * merely absent research button on a screen that otherwise looks like a draft leaves
       * somebody wondering what they are supposed to press, and the whole complaint F-92 came
       * from was presses that did nothing.
       */}
      {awaitingMaterial && (
        <>
          <h2>{t('obj.material.title')}</h2>
          {/* The same panel, one sentence apart. An uploads-only objective is told nothing is
              being looked up, which is the confirmation that the choice took; a scoped one is
              told what adding a page sets off, because for it that sentence would be false an
              hour later. See the copy itself for why the difference is load-bearing. */}
          <p className="muted">
            {t(objective.planFrom === 'scoped' ? 'obj.material.body.scoped' : 'obj.material.body')}
          </p>
          <div className="actions">
            {/* The uploader is a section away on this screen, so this scrolls to it and opens
                it — the same `goToUpload` the gap notice uses, for the same reason: a button
                that only says where to go is the third press F-92 removed. */}
            <button type="button" className="button primary" onClick={goToUpload}>
              {t('obj.material.button')}
            </button>
          </div>
          {/* The way out, said once. Somebody who chose this and finds their pages thin has to
              be able to discover that research exists without guessing that a setting hides it. */}
          <p className="muted small">
            {t(
              objective.planFrom === 'scoped'
                ? 'obj.material.switch.scoped'
                : 'obj.material.switch',
            )}
          </p>
        </>
      )}

      {drafting &&
        (objective.researchError ? (
          <>
            <h2>{t('obj.research.failed.title')}</h2>
            <p className="muted">{t('obj.research.failed.body')}</p>
            {/* The pipeline's own words, not a paraphrase. Research that died on
                an unreachable source and research that was throttled call for
                different next moves, and this string is the only thing that
                tells them apart. */}
            <p className="problem detail">{objective.researchError}</p>
          </>
        ) : (
          <>
            <h2>{t('obj.research.title')}</h2>
            <p className="muted">{t('obj.research.body')}</p>
          </>
        ))}

      {drafting && (
        <div className="actions">
          <button
            type="button"
            className="button primary"
            onClick={() => void research()}
            disabled={starting || busy}
          >
            {starting
              ? t('obj.research.starting')
              : objective.researchError
                ? t('obj.research.retry')
                : t('obj.research.start')}
          </button>
        </div>
      )}

      {(researching || building) && (
        <>
          <h2>{researching ? t('obj.research.running.title') : t('cur.building.title')}</h2>
          <p className="muted">
            {researching ? t('obj.research.running.body') : t('cur.building.body')}
          </p>
          {/*
            The wait, with something in it (F-144, F-147, F-149).
            
            **This is the screen a learner is actually sitting in front of for sixteen minutes**,
            and until now it was two sentences that never changed — the subject list, the
            character counts and the sentence about what could not be found were all on a route
            called "research and data" that nobody thinks to open while they are waiting for the
            thing they just pressed. `starting` is what puts something here in the first second,
            before any row exists to read.
          */}
          <ResearchPulse
            objectiveId={objective.objectiveId}
            progress={progress}
            starting={starting}
          />
          {waitedTooLong && (
            <>
              <p className="muted">{t('obj.wait.slow')}</p>
              <div className="actions">
                <button type="button" className="button" onClick={checkAgain}>
                  {t('obj.wait.check')}
                </button>
              </div>
            </>
          )}
        </>
      )}

      {/*
       * The failure is reported whether or not there is a curriculum to show.
       *
       * It reads on its own rather than inside the build offer below, which only
       * appears when there are no nodes: a build that failed while an older
       * curriculum was still in place would otherwise be completely silent — the
       * outline would sit there looking current, and nothing anywhere would say
       * the attempt to replace it had failed.
       */}
      {status === 'review' && objective.synthesisError && (
        <>
          <h2>{t('cur.title')}</h2>
          <p className="muted">{t('cur.failed.body')}</p>
          <p className="problem detail">{objective.synthesisError}</p>
        </>
      )}

      {canBuild && (
        <>
          {!objective.synthesisError && (
            <>
              <h2>{t('cur.title')}</h2>
              <p className="muted">{t('cur.build.body')}</p>
            </>
          )}
          {/*
            What the findings *are*, where the notice says they are in (F-145).

            Leo, 2026-09-17: *"it updates to the findings are in, build the plan but I dont know
            what the findings are unless i switch to research and data."* The numbers were on a
            route away and the sentence that announced them carried none of them — so the answer
            is the counts, the material that arrived, a paragraph of it, and one link for the
            learner who wants all of it. The bars are off because the whole subject list with
            every outcome and its two remedies is what that link leads to, and a second copy of
            it here would be the two-lists-that-disagree problem F-129 exists to have ended.
          */}
          {findings && (
            <>
              <p className="muted small">
                {t('pulse.findings')
                  .replace('{found}', String(findings.found))
                  .replace('{subjects}', String(findings.subjects))
                  .replace('{characters}', findings.characters.toLocaleString(locale))}
              </p>
              {findings.gaps > 0 && (
                <p className="muted small">
                  {t('pulse.findings.gaps').replace('{gaps}', String(findings.gaps))}
                </p>
              )}
            </>
          )}
          <ResearchPulse
            objectiveId={objective.objectiveId}
            progress={progress}
            starting={false}
            bars={false}
          />
          {findings && (
            <div className="actions">
              {/* Not `primary`: the thing to press here is the build. This is for the learner
                  who wants to read what it will be built from first, which is a reasonable
                  thing to want and was previously a route they had to know about.

                  **And it is a scroll now rather than a link** (F-141). It pointed at the
                  literal `/investigacion` — a path F-142 had already retired, so it reached the
                  research screen through a redirect, which is the sort of thing the named-route
                  table exists to stop. There is no other screen to point at any more: what was
                  read is a section of this one, a few panels down. */}
              <button type="button" className="button" onClick={goToResearch}>
                {t('pulse.seeAll')}
              </button>
            </div>
          )}
          <div className="actions">
            <button
              type="button"
              className="button primary"
              onClick={() => void synthesise()}
              disabled={starting || busy}
            >
              {starting ? t('obj.working') : t('cur.build')}
            </button>
          </div>
        </>
      )}

      {/* Two sources, one place, and never both at once in practice: `createProblem` belongs to a
          `drafting` objective whose automatic pass was refused, and `stepProblem` to a press that
          has just been refused — and the press is what would replace it. Preferred in that order
          for the same reason: the newer complaint is the one the learner just caused. */}
      {(stepProblem ?? createProblem) && (
        <p className="problem" role="alert">
          {stepProblem ?? createProblem}
        </p>
      )}
    </section>
  );

  // ---- Configuration: the one thing about a live objective that stays adjustable ----

  if (section === 'settings') {
    return (
      /*
       * The target, and not the description beside it.
       *
       * The description is the research prompt and is deliberately read-only once a pass
       * has run — changing it would silently invalidate a curriculum built from the old
       * wording — so the target is genuinely the only part of a stated objective that a
       * learner may still move. That is what makes it a setting and puts it here rather
       * than on the plan.
       */
      <>
      {/*
       * Where the material comes from, changeable after the fact (F-118).
       *
       * **A one-way choice at creation would have made this a new objective**, and starting one
       * costs the learner every review state they have built: the memory model, the schedule, the
       * history F-87 goes to considerable lengths to keep across a fold. Somebody who began with
       * photographs of a textbook and finds it thin should be able to ask for research over the
       * top of the plan they already have, and somebody who researched and would rather work from
       * their own book should be able to stop paying attention to the web.
       *
       * Above the target rather than below it because it is the more consequential of the two:
       * the target moves a dial, this decides whether anything is looked up at all.
       */}
      <Disclosure id="source" title={t('cfg.source.title')}>
        <section className="panel">
          <p className="muted">{t('cfg.source.body')}</p>
          <PlanSourceFields planFrom={planFrom} setPlanFrom={setPlanFrom} disabled={busy} />
          <div className="actions">
            <button
              type="button"
              className="button primary"
              onClick={() => void savePlanSource()}
              disabled={busy}
            >
              {busy ? t('obj.working') : t('cfg.source.save')}
            </button>
            {sourceSaved && <span className="muted">{t('cfg.source.saved')}</span>}
          </div>
        </section>
      </Disclosure>
      <Disclosure id="target" title={t('cfg.target.title')}>
        <section className="panel">
          <TargetFields
            mode={mode}
            setMode={setMode}
            score={score}
            setScore={setScore}
            date={date}
            setDate={setDate}
            disabled={busy}
          />

          {problem && (
            <p className="problem" role="alert">
              {problem}
            </p>
          )}

          <div className="actions">
            <button type="button" className="button primary" onClick={saveTarget} disabled={busy}>
              {busy ? t('obj.working') : t('obj.target.save')}
            </button>
            {saved && <span className="muted">{t('obj.target.saved')}</span>}
          </div>
        </section>
      </Disclosure>
      </>
    );
  }

  // ---- The objective: what it is doing, what it was built from, and what it has become ----

  /*
   * One screen, two faces (F-141).
   *
   * Leo, 2026-09-18: *"We should not have two sections, research and data and plan, rather have
   * that unified in a single view that either provides access to plan and research functionality,
   * or shows dynamic data while loading, a better alternative than a 15 minute loading spinner."*
   *
   * **The two routes were two answers to one question.** A learner does not have a research task
   * and a plan task; they have one objective, which is read up on and then taught. What each face
   * shows is decided by the panels themselves and not by a toggle — the step says what is
   * happening, the plan renders when there is one, and the research section is there in both
   * cases, because *what was found, and where it came from* is a permanent property of a plan and
   * not a phase of building one.
   *
   * The order is an argument, and it is the one both screens already had, concatenated at the
   * seam where they used to be separate routes:
   *
   * the objective itself; what is happening next, which during a wait is the live panel; the
   * plan; starting it; the questions; **what was read**; what is missing from it; the two ways to
   * close that, which are two sections; the material; and closing the objective. Eleven
   * `Disclosure`s, where the plan screen had six and the research screen four.
   *
   * **Eleven headings is not what anybody sees**, and that is what makes the concatenation
   * bearable. Every one of them renders nothing outside the states it belongs to and hides itself
   * when it does — `.disclosure-panel:has(> .disclosure > .disclosure-body:empty)` in `styles.css`
   * is the whole mechanism. A learner at `drafting` with nothing read sees five: the objective,
   * the step, what was read, the material and the close. The plan, the start, the questions, the
   * gaps and the two second looks all have nothing to say yet, and none of them leaves a heading
   * behind.
   */

  return (
    <>
      {tabs}
      {/*
       * The objective's name is in the body and the section's name is on the summary.
       *
       * The generated title can be a line and a half of Spanish, and a summary row is a
       * tap target with a chevron on the end of it — a heading that wraps to three lines
       * stops reading as one. The status pill rides on the summary instead, because that
       * is the one word about this objective worth seeing while the section is folded.
       */}
      <Disclosure
        id="objective"
        title={t('plan.objective.title')}
        aside={
          <span className={`pill pill-${objective.status}`}>
            {t(`obj.status.${objective.status}` as CopyKey)}
          </span>
        }
      >
        <section className="panel">
          <h3 className="objective-title">{title}</h3>

          <h3 className="label">{t('obj.current.description')}</h3>
          {/* Read-only, and shown rather than editable: it is the research prompt,
              and changing it after research has run would silently invalidate a
              curriculum built from the old wording. */}
          <p className="quoted">{objective.description}</p>
        </section>
        {status === 'active' && (
          <section className="panel">
            <p className="meta">
              <span className="pill pill-extracted">{t('start.running')}</span>
            </p>
          </section>
        )}
      </Disclosure>

      {/*
       * Whatever the objective is doing next, and nothing when it is not doing anything.
       *
       * The panel keeps its own headings rather than inheriting this one: "we are
       * researching the subject" and "the research stopped" are the whole content of it,
       * and replacing them with the word "next" would throw away the only thing it says.
       * When there is no step — a running objective on an ordinary day — the body is
       * empty and the section hides itself rather than promising something behind it.
       */}
      <Disclosure id="step" title={t('plan.step.title')}>
        {step}
      </Disclosure>

      {/*
       * The plan, the offer to build it again, and the complaint when it is missing, in
       * one section. On a phone this is sixty-nine topics of scrolling, which is exactly
       * what folding exists for — and the rebuild button is not a section of its own, it
       * is a thing to do about the list it sits under.
       */}
      <Disclosure id="curriculum" title={t('cur.title')} repeatsTitle>
        {nodes && nodes.length > 0 && (
          <Curriculum objectiveId={objective.objectiveId} nodes={nodes} coverage={coverage} />
        )}
        {/*
         * Building it again, offered whenever there is a plan and it is still under
         * review.
         *
         * The offer used to require *zero* nodes, on the reasoning that a curriculum
         * only gets built once. That left no way out of a bad one: a pass that came
         * back in the wrong language put 69 nodes on the screen and simultaneously
         * removed the only button that could replace them, so the fix needed somebody
         * with database access. Synthesis reads the content store and replaces its own
         * previous output, so re-running is exactly what it is built for — and it costs
         * cents, because the expensive half is the research it does not repeat.
         */}
        {status === 'review' && nodes && nodes.length > 0 && (
          <section className="panel">
            <p className="muted">{t('cur.rebuild.body')}</p>
            {/*
             * No error is rendered here, and that is the fix rather than an omission.
             *
             * This panel used to show `itemsProblem`, which nothing on it can produce:
             * its button calls `synthesise()`, which reports through `stepProblem`. So
             * the only thing it ever displayed was a failure from the questions panel
             * further down — the same complaint pinned to an unrelated control, which
             * is what reached Leo as "the error came up in multiple places". The
             * failure this panel can actually produce is already shown beside the
             * status, where a rebuild's progress is read from.
             */}
            <div className="actions">
              <button
                type="button"
                className="button"
                onClick={() => void synthesise()}
                disabled={starting || busy}
              >
                {starting ? t('obj.working') : t('cur.rebuild')}
              </button>
            </div>
          </section>
        )}
        {/* An objective that is running with nothing to run on. It should not
            happen, and saying so is better than a screen that quietly omits the
            plan the learning loop is supposedly working from. */}
        {status === 'active' && nodes?.length === 0 && (
          <section className="panel">
            <h2>{t('cur.title')}</h2>
            <p className="muted">{t('cur.empty')}</p>
          </section>
        )}
      </Disclosure>

      <Disclosure id="start" title={t('start.title')} repeatsTitle>
        {/*
         * The last gate before anything sends.
         *
         * Offered only when there is genuinely something to send: a plan, questions
         * written for it, and a status still under review. Showing it earlier would
         * offer a button whose only possible outcome is an error explaining what is
         * missing — which the screen already says, above, where the missing thing is.
         */}
        {status === 'review' && nodes && nodes.length > 0 && objective.itemVersion && (
          <section className="panel">
            <h2>{t('start.title')}</h2>
            <p className="muted">{t('start.body')}</p>
            {startProblem && (
              <p className="problem" role="alert">
                {startProblem}
              </p>
            )}
            <div className="actions">
              <button
                type="button"
                className="button primary"
                onClick={() => void start()}
                disabled={starting_ || busy}
              >
                {starting_ ? t('start.working') : t('start.button')}
              </button>
            </div>
          </section>
        )}
      </Disclosure>

      <Disclosure id="items" title={t('items.title')} repeatsTitle>
        {/*
         * Offered only once there is a plan to write questions about, and shown on
         * `active` too — a running objective is exactly where somebody notices the
         * questions are thin and wants them rewritten.
         */}
        {nodes && nodes.length > 0 && (status === 'review' || status === 'active') && (
          <section className="panel" id={QUESTIONS_PANEL_ID} tabIndex={-1}>
            <h2>{t('items.title')}</h2>

            {objective.itemsError ? (
              <>
                <p className="muted">{t('items.failed.body')}</p>
                <p className="problem detail">{objective.itemsError}</p>
              </>
            ) : objective.itemVersion ? (
              <p className="meta">
                <span className="pill pill-extracted">{t('items.ready')}</span>{' '}
                {/* The pipeline's own count, not a number this screen worked out: it
                    knows how many it wrote, and a second opinion computed from a
                    paginated read is a second thing that can be wrong. */}
                {objective.itemsDetail}
              </p>
            ) : (
              <p className="muted">{t('items.build.body')}</p>
            )}

            {itemsProblem && (
              <p className="problem" role="alert">
                {itemsProblem}
              </p>
            )}
            {itemsNote && (
              <p className="muted" role="status">
                {itemsNote}
              </p>
            )}

            {/* Refused by the API while a fold holds the lock, because the plan these
                questions would be written for is the one being replaced. Said here
                rather than left to that refusal: an error explaining a button that
                should not have been offered is a worse answer than not offering it. */}
            {foldingNow && <p className="muted">{t('items.folding')}</p>}

            <div className="actions">
              <button
                type="button"
                className={objective.itemVersion ? 'button' : 'button primary'}
                onClick={() => void writeItems()}
                disabled={writingItems || busy || foldingNow}
              >
                {writingItems
                  ? t('items.working')
                  : objective.itemVersion
                    ? t('items.rebuild')
                    : t('items.build')}
              </button>
            </div>
          </section>
        )}
      </Disclosure>

      {/*
       * What was read, and what it said — the research face of the one objective view (F-141).
       *
       * **Wrapped now, where it used to be the one thing on this screen that was not.** It was the
       * whole of `/research` and could stand unwrapped above the sections that argue about it; here
       * it is a section among them and needs a heading that folds like the rest, or a learner who
       * only ever comes to this screen for the plan scrolls past twelve subjects to reach it.
       *
       * The cost is that the research *document* — tens of thousands of words, which carries a fold
       * of its own for that reason — is now two folds deep rather than one. That is the right
       * thing to have made further away: it is the exact text the plan was built from, kept for
       * checking rather than for reading, and everything a learner actually acts on is in the
       * panel above it.
       *
       * It keeps the words the menu used, because those are the words a learner already knows this
       * by. What changed is that *"Research and data"* names a part of their plan rather than a
       * place to go and look for one.
       *
       * Below the plan and above the three things to do about the material. The order is the
       * argument: what this objective has become, then what it was built from, then what is missing
       * from that and the two ways to close it.
       */}
      {/* The id is the jump target for both "see what was read" buttons, the URL fragment and
          the storage key, all three by construction — see `RESEARCH_PANEL_ID`. Nothing wraps
          `Research`, deliberately: `.disclosure-body > .panel` flattens a wrapped component's
          card, and one extra element in between would give every panel in here a second border
          with nothing reporting it. */}
      <Disclosure id={RESEARCH_PANEL_ID} title={t('nav.data')}>
        <Research
          objectiveId={objective.objectiveId}
          progress={progress}
          coverage={coverage}
          uploadsOnly={isUploadsOnly(objective)}
          /* So this section cannot say "nothing has been read" under a step panel that has just
             said twelve subjects are being read (F-144's window). */
          starting={starting}
          /*
           * The escape for a research wait, offered here because this is the panel describing it
           * — and suppressed while the step panel above is offering its own copy.
           *
           * It is what covers the two waits no other panel reaches: a `scoped` objective
           * deepening a subject, which has no *look again* panel because F-118 gates that on
           * `offersResearch`; and any objective whose thirty-minute research lock has been
           * stripped as stale while the wave is still running. `Research` adds the run's own
           * state to this, so a finished run never offers it.
           */
          stalled={waitedTooLong && !stepIsWaiting}
          onCheck={checkAgain}
          /* One live panel on the screen: the step above owns it while the learner is waiting
             or being offered the build. See `ownsPulse`. */
          pulse={ownsPulse(nextMove)}
          onChanged={load}
        />
      </Disclosure>
      {/*
       * What the material does not cover, then the two ways to close it — a page of
       * your own, or another research pass — then the uploader itself. The order is
       * the one this screen has always had, and it is an argument: the notice asks for
       * a photograph, the pass underneath is the alternative for when there is no book
       * to photograph, and the fold is what makes either of them reach the plan.
       */}
      <Disclosure id="gaps" title={t('gap.title')} repeatsTitle>
        {/*
         * First of the three things that can be done about the material, because it is
         * the only one that says what is actually missing.
         *
         * It used to be placed by argument about scroll depth — above a plan that is
         * sixty-nine topics long, because anything under that is effectively not on the
         * screen. The plan is above it again since F-141 merged the two screens, and the
         * argument stays retired: every section here folds, and the learner's own choice about
         * which ones are open is remembered. The order it produced is still right — what is
         * missing, then the two ways to supply it.
         */}
        <Gaps gaps={objective.gaps} status={objective.status} />
      </Disclosure>
      <Disclosure id="again" title={t('again.title')} repeatsTitle>
        {/*
         * The other way to close a gap: have nabu look again rather than photograph a
         * page yourself.
         *
         * Directly under the gap notice, because that notice raises exactly this
         * question — it asks for a photograph of a book, and the obvious reply is
         * "why don't you go and find it?". The honest answer is that it can, that it
         * costs real money, and that it may come back with nothing new. All three are
         * on the panel rather than in a help page.
         *
         * Offered at `review` and at `active` alike, and the same in both: the pass
         * adds to the content store and touches neither the plan nor the questions,
         * so there is nothing about a running objective that makes it more dangerous
         * — which is precisely why the status is not allowed to move for it.
         */}
        {/*
         * `researchOffered` is the addition (F-118), and it is the same gate the API applies.
         *
         * An objective built from the learner's own pages has no outline to fill gaps in and no
         * subjects to go deeper on; `planReresearch` refuses both second looks with
         * `no_research`. Rendering the control anyway would put the most expensive button in
         * the product in front of somebody whose only possible answer is a refusal explaining a
         * setting they made on purpose.
         */}
        {researchOffered && (status === 'review' || status === 'active') && (
          <section className="panel">
            <h2>{t('again.title')}</h2>
            <p className="muted">{lookingNow ? t('again.running') : t('again.body')}</p>
            {!lookingNow && (
              <p className="muted small">{t('again.honest')}</p>
            )}

            {/* The half-hour lock, said out loud. An unexplained disabled button is
                how somebody decides the feature is broken and reloads until it is. */}
            {lookingNow && <p className="muted small">{t('again.locked')}</p>}

            {lookProblem && (
              <p className="problem" role="alert">
                {lookProblem}
              </p>
            )}

            {/*
             * No "check now" here, and that is the fix rather than an omission.
             *
             * This panel had one, gated on the *lock* rather than on the run — and the API strips
             * that lock after thirty minutes, against waves it runs for up to 109, so the button
             * went away while the wait it described carried on. The research section below carries
             * the escape for every research wait now, gated on the run's own state, which is the
             * thing that is actually still happening. A second copy here would be two identical
             * buttons starting the same poll.
             */}

            {!lookingNow && <p className="muted small">{t('again.next')}</p>}

            {/* Optional, and the one control here that changes what the pass actually
                searches for. Left empty it still runs against the gaps synthesis
                recorded — which is the difference between "look again" and "look for
                this", and the reason F-109 made re-research two routes rather than one
                button. */}
            {!lookingNow && (
              <label className="field">
                <span>{t('again.note.label')}</span>
                <input
                  type="text"
                  value={lookNote}
                  maxLength={200}
                  placeholder={t('again.note.placeholder')}
                  onChange={(e) => setLookNote(e.target.value)}
                />
              </label>
            )}

            <div className="actions">
              {/* Never `primary`. The expensive button is not the one the eye lands
                  on: on this screen the thing to press is the upload or the fold,
                  and this is the fallback for when neither will do. */}
              <button
                type="button"
                className="button"
                onClick={() => void lookAgain()}
                disabled={lookingNow || foldingNow || busy}
              >
                {looking ? t('again.working') : t('again.button')}
              </button>
            </div>
          </section>
        )}
      </Disclosure>
      <Disclosure id="fold" title={t('fold.title')} repeatsTitle>
        {/*
         * What the gap notice promises, in the same section of the app as the notice.
         *
         * Until F-87 the notice was a dead end on a running objective: it asked for a
         * photograph of a page and there was no button anywhere that would put that
         * page into the plan, because synthesis refused anything past `review`. This is
         * that button. It belongs beside the material rather than beside the plan
         * because what it acts on is everything that has been added since the plan was
         * built — which is what the two sections above it are for.
         *
         * Only with a plan to add to. On a running objective without one, the plan
         * section already says the objective is broken, and offering to extend nothing
         * would be the second confusing thing to tell somebody.
         */}
        {status === 'active' && nodes && nodes.length > 0 && (
          <section className="panel">
            <h2>{t('fold.title')}</h2>
            {/*
              * One sentence for whichever of the three things is happening.
              *
              * The chain runs on its own now, so "what is going on" has three answers
              * rather than two — folding, writing the questions for what was folded, and
              * nothing in flight — and the middle one is new. Leaving it out is what made
              * the learner press a button to find out.
              */}
            <p className="muted">
              {foldingNow
                ? t('fold.running')
                : writingQuestions
                  ? t('fold.writing')
                  : t('fold.body')}
            </p>

            {/*
             * A fold that failed says so here, and nowhere else would.
             *
             * `synthesisError` is otherwise rendered only under `review`, inside the
             * step panel — and a live pass never reaches `review`, or any other
             * status, by design. Without this a fold could fail and leave the screen
             * looking exactly like one that had not been asked for: the plan intact,
             * the questions still arriving, and no sign anywhere that the page the
             * learner photographed was never read. That is this project's
             * characteristic failure and it is not being added to here.
             *
             * Only while nothing is running, because a new pass has not cleared the
             * previous one's error yet and reporting both at once says the thing in
             * progress has already failed.
             */}
            {!foldingNow && objective.synthesisError && (
              <>
                <p className="muted">{t('fold.failed')}</p>
                <p className="problem detail">{objective.synthesisError}</p>
              </>
            )}

            {/*
             * What the pass did, and the step without which it changes nothing.
             *
             * The detail line is the pipeline's own count — "0 new topics, 4
             * rewritten, 1 retired" — because a second opinion computed on this screen
             * is a second thing that can be wrong. The sentence under it is the part
             * that matters: a topic with no questions written for it is never sent, so
             * a learner left admiring an updated plan would wait for material that
             * cannot arrive.
             */}
            {foldSettled && !objective.synthesisError && (
              <p className="muted" role="status">
                {/*
                  * The whole chain as one outcome, which is the point of F-92.
                  *
                  * It used to be two technical strings and a button. `synthesisDetail`
                  * said "3 new topics, 1 rewritten, 0 retired" here and `itemsDetail`
                  * said "12 items across 4 nodes" two panels further down, and the step
                  * between them was a control the learner had to find and press — so what
                  * they were told was how the pipeline is built rather than what happened.
                  * These are the same numbers those strings are made of, said once.
                  *
                  * Three cases and not one, because "0 new topics, 8 new questions" is
                  * what a fold that only reworded things would otherwise say, and
                  * "nothing changed" is a real and common outcome that must not read as a
                  * failure — it is what re-reading material already in the plan does.
                  */}
                {foldTouched === 0
                  ? t('fold.nothingNew')
                  : foldAdded === 0
                    ? t('fold.reworded')
                        .replace('{topics}', String(foldChanged))
                        .replace('{questions}', String(objective.itemsWritten ?? 0))
                    : t('fold.outcome')
                        .replace('{topics}', String(foldAdded))
                        .replace('{questions}', String(objective.itemsWritten ?? 0))}
              </p>
            )}

            {foldProblem && (
              <p className="problem" role="alert">
                {foldProblem}
              </p>
            )}

            {/* The same "this page has stopped watching" escape as research and the
                build. Shared with them safely because the two can never be on screen
                together: this panel renders only at `active`, and `planStep` answers `none`
                there, so the step panel that carries the other copies is not rendered.

                Offered while the *questions* are being written too, and that is the half
                that matters now: generation runs for minutes on the real-time path and
                can run for hours on a batch, so the wait a learner is most likely to
                outlast is this one. */}
            {(foldingNow || writingQuestions) && waitedTooLong && (
              <>
                <p className="muted">{t('obj.wait.slow')}</p>
                <div className="actions">
                  <button type="button" className="button" onClick={checkAgain}>
                    {t('obj.wait.check')}
                  </button>
                </div>
              </>
            )}

            <div className="actions">
              {/*
                * Still offered, because not every source folds itself in: a re-research
                * pass adds a document to the content store and no extraction fires for
                * it, so this is the only way that material reaches the plan.
                *
                * Refused while the questions for the last fold are still being written,
                * which is new and is not cosmetic. A fold mints a new
                * `curriculumVersion`, and the run in flight is writing items stamped with
                * the one before it — so the next generation would find those items older
                * than their nodes and prune the questions this one just paid for. Before
                * the chain existed this window had to be opened by hand; now it is open
                * after every single fold.
                */}
              <button
                type="button"
                className="button"
                onClick={() => void fold()}
                disabled={foldingNow || writingQuestions || busy}
              >
                {foldingNow ? t('fold.working') : t('fold.button')}
              </button>
            </div>
          </section>
        )}
      </Disclosure>
      <Disclosure id="material" title={t('src.title')} repeatsTitle>
        {/* Told that something is wanted, so the panel a learner lands on from the
            gap notice does not greet him with the word "Optional". */}
        <Sources
          objectiveId={objective.objectiveId}
          wanted={(objective.gaps?.length ?? 0) > 0 && GAP_STATUSES.includes(objective.status)}
          /* An upload folds itself in only once there is a plan to fold it into, which is
             exactly what `curriculumVersion` says — the same condition `planAutoFold` in
             `packages/ingest/src/extract.ts` gates on, so the screen cannot promise
             something the pipeline will refuse. */
          automatic={Boolean(objective.curriculumVersion)}
          /* An extraction finishing is what starts the fold, so it is also the moment this
             screen has to look again — see `onExtraction`. Without it the panel above goes
             on offering to fold material that is already being folded. */
          onExtraction={load}
        />
      </Disclosure>

      <Disclosure id="close" title={t('plan.close.title')}>
        <section className="panel">
          <p className="muted">{t('obj.close.hint')}</p>
          <div className="actions">
            <button type="button" className="button" onClick={() => close('done')} disabled={busy}>
              {t('obj.close.done')}
            </button>
            <button type="button" className="button" onClick={() => close('abandoned')} disabled={busy}>
              {t('obj.close.abandon')}
            </button>
          </div>
        </section>
      </Disclosure>

      {history}
    </>
  );
}
